import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDataEngine } from '@dhis2/app-runtime';
import { Button, Input, TextArea, SingleSelect, 
  SingleSelectOption, AlertBar, CircularLoader,
   Card, Modal, ModalTitle, ModalContent, ModalActions, IconSearch24
   } from '@dhis2/ui';
import { DndProvider, useDrag} from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  shouldShowObjective,
  getCurrentUserWithRole,
} from './utils/userRoleUtils';
import { useNavigate } from "react-router-dom";
import { isWithinFiscalYear, resolveActiveFiscalYear } from '../GoalsManagement/utils/fiscalYearHelpers';
import { ActivityIcon, CheckCircleIcon, CheckIcon } from "lucide-react";
import { GiNotebook, GiProgression } from "react-icons/gi";
import { TbHandFingerDown, TbPointerDown } from "react-icons/tb";


// Data Element Mappings for Tasks program stage
const TASK_DATAELEMENT_MAPPING = {
  selectedWeek: 'EiL3lPaL908',
  progressPercent: 'AElpNxzTdRh',
  status: 'LuTwfuN34pc',
  achievements: 'j9FdNkmPpXM',
  challenges: 'WSQ9nuXPnNL',
  nextSteps: 'M7PG2p2XsDA',
  objectiveId: 'plJovUlxfQ0',
  taskName: 'I0QtZkMbzxN',
  taskDescription: 'LR70eqTjB8m',
  kpiId: 'uXeH0X0aaCl',
  positionTei: 'SlgO60cMK3F',
  incumbentStaffId: 'OHKFI8GuxPs',
  subTasks: 'YBXgnhyue59'
};



const KPI_PROGRAM_STAGE_ID = 'ivyCLjNLFrd';
const KPI_PROGRAM_ID = 'yiT00XKrrHP';

const TASK_PROGRAM_STAGE_ID = 'Nv9nYNcBTdM';
const PROGRAM_ID = 'yiT00XKrrHP';

// Auto-save configuration
const AUTO_SAVE_DELAY = 3000; // 3 seconds
const DRAFT_NAMESPACE = 'weekly-progress-drafts';
const OBJECTIVE_DATA_ELEMENTS = {
  START_DATE: 'eDcVy661j9e',
  COMPLETION_DATE: 'J0rjxrDKn1l',
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
@keyframes pulse-amber {
  0%, 100% { 
    background-color: rgba(241, 142, 13, 0.3);
    box-shadow: 0 0 0 0 rgba(241, 142, 13, 0.7);
  }
  50% { 
    background-color: rgba(241, 142, 13, 0.6);
    box-shadow: 0 0 0 4px rgba(241, 142, 13, 0);
  }
}

@keyframes pulse-green {
  0%, 100% { 
    background-color: rgba(40, 167, 69, 0.3);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  50% { 
    background-color: rgba(40, 167, 69, 0.6);
    box-shadow: 0 0 0 4px rgba(40, 167, 69, 0);
  }
}

@keyframes pulse-blue {
  0%, 100% { 
    background-color: rgba(23, 162, 184, 0.3);
    box-shadow: 0 0 0 0 rgba(23, 162, 184, 0.7);
  }
  50% { 
    background-color: rgba(23, 162, 184, 0.6);
    box-shadow: 0 0 0 4px rgba(23, 162, 184, 0);
  }
}

  input[type="range"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    height: 8px;
    border-radius: 4px;
    background: #e9ecef;
    outline: none;
    width: 100%;
    margin: 10px 0;
    position: relative;
  }

  /* WebKit (Chrome, Safari) */
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6c757d;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,1);
    pointer-events: auto;
  }

  input[type="range"]:hover::-webkit-slider-thumb {
    background: #555;
  }

  /* Firefox */
  input[type="range"]::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6c757d;
    cursor: pointer;
    border: none;
    box-shadow: 0 4px 8px rgba(0,0,0,1);
    pointer-events: auto;
  }

  input[type="range"]:hover::-moz-range-thumb {
    background: #555;
  }

  input[type="range"]::-moz-range-track {
    background: #e9ecef;
    border-radius: 4px;
    height: 8px;
  }
    @keyframes point {
    0%, 100% {
      transform: translateX(0);
      opacity: 1;
    }
    25% {
      transform: translateX(10px);
      opacity: 0.8;
    }
    50% {
      transform: translateX(-5px);
      opacity: 1;
    }
    75% {
      transform: translateX(5px);
      opacity: 0.9;
    }
  }
  
  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  .finger-pointer {
    animation: point 1.5s ease-in-out infinite;
    display: inline-block;
    font-size: 2rem;
  }
  
  .kpi-card-highlight {
    transition: all 0.3s ease;
    cursor: pointer;
  }
  
  .kpi-card-highlight:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  }
  
  @keyframes pulse-ring {
    0% {
      box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(231, 76, 60, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(231, 76, 60, 0);
    }
  }
  
  .pulse-animation {
    animation: pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
  }
`;
document.head.appendChild(styleSheet);

// Draggable KPI Card Component
const DraggableKPICard = ({ kpi, isSelected, onSelect }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'kpi',
    item: { kpi },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        marginBottom: '10px',
        marginTop: '10px',
        marginLeft: '10px',
        boxShadow: "0 8px 10px rgba(0, 0, 0, 0.3)",
        borderRadius: '14px',
        backgroundColor: 'rgb(230, 237, 152)'

      }}
      onClick={() => onSelect(kpi)}
    >
      <Card
        style={{
          border: isSelected ? '2px solid #e74c3c' : '1px solid #ddd',
          backgroundColor: isSelected ? 'rgb(242, 245, 206)' : 'white',
        }}
      >
        <div style={{ padding: '12px', borderRadius: '8px' }}>
          <h5 style={{ margin: '0 0 4px 0', color: '#2c3e50' }}>
            {kpi.name}
          </h5>
          {kpi.description && (
            <p style={{ margin: '0 0 4px 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
              {kpi.description}
            </p>
          )}
          <div style={{ display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.7rem',
                        color: '#95a5a6' }}>
            <span>Target: {kpi.target || 'N/A'}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Update the TaskDetailModal component to show KPI
const TaskDetailModal = ({ task, statusOptions, onClose }) => {
  if (!task) return null;

  return (
    <Modal large onClose={onClose}>
      <ModalTitle>Task Progress Details</ModalTitle>
      <ModalContent>
        <div style={{ padding: '16px' }}>
          {/* Add KPI section */}
          {task.kpi && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e8f4fd',
              borderRadius: '8px',
              marginBottom: '16px',
              borderLeft: '4px solid #2980b9',
              boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
            }}>
              <strong>Linked KPI:</strong>
              <div style={{ marginTop: '4px' }}>
                <strong>{task.kpi.name}</strong>
                {task.kpi.target && (
                  <span style={{ marginLeft: '8px', color: '#666' }}>
                    (Target: {task.kpi.target})
                  </span>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <strong>Week Ending:</strong> {task.selectedWeek}
            </div>
            <div>
              <strong>Progress:</strong> {task.progressPercent}%
            </div>
            <div>
              <strong>Status:</strong> {statusOptions.find(s => s.value === task.status)?.label || task.status}
            </div>
          </div>

          {/* Rest of your existing modal content remains the same */}
          {task.taskName && (
            <div style={{ marginBottom: '16px' }}>
              <strong>Task Name:</strong>
              <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginTop: '4px' }}>
                {task.taskName}
              </div>
            </div>
          )}
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Close</Button>
      </ModalActions>
    </Modal>
  );
};

// ============================================================================
// EMAIL NOTIFICATION UTILITIES - Task-aware duplicate prevention
// ============================================================================

// Generate unique key for task+week+content
const getChallengeEmailKey = (task, week, challenges) => {
  const taskId = task?.eventId || task?.id || 'new';
  const contentHash = simpleHash(challenges?.trim() || '');
  return `challenge_email_${taskId}_${week}_${contentHash}`;
};

// Simple hash function for content deduplication
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36).substring(0, 8);
};

  // Check if email should be sent (with cooldown logic)
  const shouldSendChallengeEmail = (key, minCooldownMs = 15 * 60 * 1000) => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return { shouldSend: true, reason: 'first_time' };
        
        const data = JSON.parse(stored);
        const now = Date.now();
        
        // Allow if cooldown period has passed
        if (now >= data.cooldownUntil) {
          return { shouldSend: true, reason: 'cooldown_expired' };
        }
        
        // Block if still in cooldown
        return {
          shouldSend: false,
          reason: 'in_cooldown',
          remainingMs: data.cooldownUntil - now,
          lastSent: data.lastSent
        };
      } catch (e) {
        console.warn('⚠️ [EMAIL] Failed to parse tracking data, allowing send:', e);
        return { shouldSend: true, reason: 'parse_error_fallback' };
      }
    };

    // Mark email as sent (with cooldown)
    const markChallengeEmailSent = (key, cooldownMs = 15 * 60 * 1000) => {
      try {
        const data = {
          lastSent: Date.now(),
          cooldownUntil: Date.now() + cooldownMs,
          version: 1
        };
        localStorage.setItem(key, JSON.stringify(data));
        console.log('✅ [EMAIL] Marked as sent with cooldown:', { key, cooldownMs });
      } catch (e) {
        console.warn('⚠️ [EMAIL] Failed to store tracking data:', e);
      }
    };

// Optional: Clear tracking for a specific task (useful for testing)
const clearChallengeEmailTracking = (taskId, week) => {
  const prefix = `challenge_email_${taskId || ''}_${week || ''}`;
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(prefix)) {
      localStorage.removeItem(key);
      console.log('🗑️ [EMAIL] Cleared tracking:', key);
    }
  });
};

const getCurrentCalendarYearRange = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  return {
    start: `${currentYear}-01-01`,
    end: `${currentYear}-12-31`,
  };
};

const objectiveBelongsToFiscalYear = (objective, fiscalYear) => {
  if (!fiscalYear?.start || !fiscalYear?.end) return true;

  const startDate = objective.startDate || objective.eventDate || '';
  const completionDate = objective.completionDate || '';

  if (!startDate && !completionDate) {
    return false;
  }

  return (
    (startDate && isWithinFiscalYear(startDate, fiscalYear)) ||
    (completionDate && isWithinFiscalYear(completionDate, fiscalYear))
  );
};


    // Draggable Objective Card Component
    const DraggableObjectiveCard = ({ 
      objective, 
      isSelected, 
      onSelect, 
      hasSubmittedTaskThisWeek, 
      hasDraftThisWeek, 
      isWeekLocked,
      currentTasks, // Add this prop to pass current tasks
      selectedWeek  // Add this prop
    }) => {
      const [{ isDragging }, drag] = useDrag(() => ({
        type: 'objective',
        item: { objective },
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
      }));

      // Inside DraggableObjectiveCard component, fix the hasActivityContent useMemo:
      const hasActivityContent = useMemo(() => {
        if (currentTasks && currentTasks.length > 0) {
          return currentTasks.some(task => {
            // Check for ANY content, not just activities
            const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task' && task.taskName !== '';
            const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
            const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
            const hasChallenges = task.challenges?.trim() && task.challenges.trim().length > 5;
            const hasActivities = task.subTasks && task.subTasks.length > 0;
            const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;
            
            return hasValidTaskName || hasTaskDescription || hasAchievements || 
                  hasChallenges || hasActivities || hasProgress;
          });
        }
        return hasDraftThisWeek;
      }, [currentTasks, hasDraftThisWeek]);

      // Determine status display
      let statusDisplay = null;
      
      if (hasSubmittedTaskThisWeek && isWeekLocked) {
        statusDisplay = { label: 'TASK EXISTS', color: '#ffc107', bgColor: '#ffc107', textColor: '#000' };
      } else if (hasSubmittedTaskThisWeek || hasActivityContent) {
        statusDisplay = { label: '✎ TOUCHED', color: '#17a2b8', bgColor: '#17a2b8', textColor: '#fff' };
      } else if (!isWeekLocked) {
        statusDisplay = { label: '⚠ NOT TOUCHED', color: '#ffc107', bgColor: 'transparent', textColor: '#fdfdfd', animate: true };
      }

      return (
        <div
          ref={drag}
          style={{
            opacity: isDragging ? 0.5 : 1,
            cursor: 'move',
            marginBottom: '12px',
          }}
          onClick={() => onSelect(objective)}
        >
          <Card
            style={{
              border: isSelected ? '2px solid #08324eff' : '1px solid #ddd',
              backgroundColor: isSelected ? '#135385ff' : 'white',
              transition: 'all 0.2s ease',
              boxShadow: "0 4px 18px rgba(14, 13, 13, 1)",
            }}
          >
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#2c3e50' }}>
                  {objective.name}
                </h4>
                <div style={{ display: 'flex', gap: '4px', flexDirection: 'column', alignItems: 'flex-end' }}>
                  {statusDisplay && (
                    <span style={{
                      padding: '3px 8px',
                      backgroundColor: statusDisplay.bgColor || statusDisplay.color,
                      color: statusDisplay.textColor || '#fff',
                      borderRadius: '8px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      ...(statusDisplay.animate && {
                        animation: 'pulse-amber 2s ease-in-out infinite'
                      })
                    }}>
                      {statusDisplay.label}
                    </span>
                  )}
                </div>
              </div>
              <p style={{
                margin: '0 0 8px 0',
                color: '#7f8c8d',
                fontSize: '0.9rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {objective.description || 'No description available'}
              </p>
              <div style={{
                fontSize: '0.8rem',
                color: '#95a5a6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{objective.orgUnitName}</span>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: isSelected ? '#2980b9' : '#ecf0f1',
                  color: isSelected ? 'white' : '#7f8c8d',
                  borderRadius: '12px',
                  fontSize: '0.7rem'
                }}>
                  Start Date:
                  {objective.startDate ? new Date(objective.startDate).toLocaleDateString() : 'No date'}
                </span>
              </div>
            </div>
          </Card>
        </div>
      );
  };

const ObjectiveSelectionDisplay = ({ selectedObjective, onClear, hasSubmittedTaskThisWeek }) => {
  return (
    <div
      style={{
        padding: '20px',
        border: `2px solid ${selectedObjective ? '#27ae60' : '#bdc3c7'}`,
        borderRadius: '8px',
        backgroundColor: selectedObjective ? '#e8f8ef' : '#f8f9fa',
        textAlign: 'center',
        marginBottom: '20px',
        transition: 'all 0.3s ease',
        minHeight: '40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      {selectedObjective ? (
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#27ae60', fontSize: '1.0rem' }}>
            <CheckIcon size={16}/> Objective Selected
          </h4>
          <p style={{ 
            margin: '0 0 16px 0', 
            fontWeight: 'bold',
            fontSize: '1.15rem',
            color: '#2c3e50'
          }}>
            {selectedObjective.name}
          </p>
          {hasSubmittedTaskThisWeek && (
            <div style={{
              padding: '10px',
              backgroundColor: '#fff8e1',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #ffc107',
              fontWeight: 500
            }}>
              📌 You already have tasks for this week
            </div>
          )}
          <Button 
            small 
            destructive 
            onClick={onClear}
            style={{ minWidth: '160px' }}
          >
            Change Objective
          </Button>
        </div>
      ) : (
        <div>
          <p style={{ 
            margin: '0 0 8px 0', 
            color: '#2c3e50', 
            fontWeight: 'bold',
            fontSize: '0.9rem'
          }}>
            Select an Objective Below
          </p>
          <p style={{ 
            margin: 0, 
            fontSize: '0.8rem', 
            color: '#7f8c8d',
            maxWidth: '300px'
          }}>
            Click any objective card to begin tracking weekly progress
          </p>
        </div>
      )}
    </div>
  );
};
// Auto-save Indicator Component
const AutoSaveIndicator = ({ isAutoSaving, hasUnsavedChanges, lastAutoSaved, isFormDisabled }) => {
  if (isFormDisabled) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14 px',
      color: '#07611bff',
      marginBottom: '24px',
      padding: '8px',
      backgroundColor: '#c5c9cdff',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
      boxShadow: "0 8px 10px rgba(192, 194, 192, 1)",
    }}>
      {isAutoSaving ? (
        <>
          <CircularLoader small />
          <span>Auto-saving your progress...</span>
        </>
      ) : hasUnsavedChanges ? (
        <>
          <span style={{ color: '#ffc107' }}>●</span>
          <span>Unsaved changes - auto-saving in {AUTO_SAVE_DELAY / 1000}s</span>
        </>
      ) : lastAutoSaved ? (
        <>
          <span style={{ color: '#28a745' }}>✓</span>
          <span>All changes saved {lastAutoSaved.toLocaleTimeString()}</span>
        </>
      ) : (
        <>
          <span style={{ color: '#6c757d' }}>ⓘ</span>
          <span>Start typing - your progress will be auto-saved</span>
        </>
      )}
    </div>
  );
};

// Helper: Get color based on progress percentage
const getProgressColor = (progress) => {
  if (progress >= 67) return '#28a745'; // Green
  if (progress >= 34) return '#ffc107'; // Amber/Yellow
  return '#dc3545'; // Red
};

const canAddNewTask = (weekDate) => {
  const now = new Date();
  const targetWeek = new Date(weekDate);
  const lockDate = new Date(targetWeek);
  lockDate.setDate(lockDate.getDate() + 7); // 7-day grace period
  return now <= lockDate;
};

// Check if week is in "view-only" mode (beyond 7 days)
const isViewOnlyWeek = (weekDate) => {
  return !canAddNewTask(weekDate);
};


const getSliderFillColor = (progress) => {
  if (progress >= 67) return '#28a745'; // Green
  if (progress >= 34) return '#ffc107'; // Amber/Yellow
  return '#dc3545'; // Red
};

  // Add this helper function at the top of the WeeklyProgressTracker component
  const isPastWeek = (weekDate) => {
      if (!weekDate) return false;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const targetWeek = new Date(weekDate);
      const weekEndDate = new Date(targetWeek);
      weekEndDate.setDate(weekEndDate.getDate() + 1);
      return weekEndDate < today;
  };

  
   // Replace your SubTask component with this fixed version
    const SubTask = React.memo(({ 
      id, 
      description, 
      progressPercent, 
      completed, 
      index, 
      onUpdate, 
      onRemove, 
      isFormDisabled,
      selectedWeek,
      showNotification
    }) => {
      const [showConfirmDialog, setShowConfirmDialog] = useState(false);
      const [localDescription, setLocalDescription] = useState(description);
      const debounceTimerRef = useRef(null);
      const isLocked = completed || isFormDisabled;
      
      // Update local state when prop changes (for when loading existing tasks)
      useEffect(() => {
        setLocalDescription(description);
      }, [description]);

      const handleDescriptionChange = useCallback(({ value }) => {
        if (!isLocked) {
          // Update local state immediately for responsive UI
          setLocalDescription(value);
          
          // Clear previous timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          
          // Set new timer
          debounceTimerRef.current = setTimeout(() => {
            onUpdate(index, { description: value });
          }, 500);
        }
      }, [isLocked, onUpdate, index]);

      // Cleanup on unmount
      useEffect(() => {
        return () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
        };
      }, []);

      
      const handleProgressChange = useCallback((e) => {
        if (isLocked) return;
        const newProgress = Number(e.target.value);
        if (newProgress === 100) {
          setShowConfirmDialog(true);
        } else {
          onUpdate(index, { progressPercent: newProgress, completed: false });
        }
      }, [isLocked, onUpdate, index]);
      
      const confirmCompletion = useCallback(() => {
        const isPastWeekCompletion = isPastWeek(selectedWeek);
        
        if (isPastWeekCompletion) {
          if (window.confirm('You are completing a task from a past week. This will lock the task. Continue?')) {
            onUpdate(index, { progressPercent: 100, completed: true });
            setShowConfirmDialog(false);
            if (showNotification) {
              showNotification('✓ Task marked as complete for past week', 'success');
            }
          } else {
            setShowConfirmDialog(false);
          }
        } else {
          onUpdate(index, { progressPercent: 100, completed: true });
          setShowConfirmDialog(false);
        }
      }, [index, onUpdate, selectedWeek, showNotification]);
      
      const cancelCompletion = useCallback(() => {
        setShowConfirmDialog(false);
      }, []);

      const handleRemove = useCallback(() => {
        onRemove(index);
      }, [onRemove, index]);

      const renderFormField = (label, children, required = true) => (
        <div style={{ marginBottom: '5px' }}>
          <div style={{ fontWeight: '400', marginBottom: '4px' }}>
            {label} {required && <span style={{ color: 'red' }}>*</span>}
          </div>
          {children}
        </div>
      );

      return (
        <>
          <div style={{
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            marginBottom: '8px',
            backgroundColor: completed ? '#e8f5e9' : '#f9f9f9',
            opacity: isLocked ? 0.9 : 1,
            position: 'relative'
          }}>
            {completed && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                backgroundColor: '#28a745',
                color: 'white',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                🔒
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                {renderFormField('Activity Description', (
                  <TextArea
                    value={localDescription}
                    onChange={handleDescriptionChange}
                    placeholder="Describe this activity"
                    rows={2}
                    disabled={isLocked}
                    required
                  />
                ))}
              </div>
              {!completed && (
                <Button
                  small
                  destructive
                  onClick={handleRemove}
                  disabled={isFormDisabled}
                  style={{ 
                    marginLeft: window.innerWidth < 768 ? '0' : '8px',
                    marginTop: window.innerWidth < 768 ? '8px' : '0',
                    width: window.innerWidth < 768 ? '100%' : 'auto'
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  color: getProgressColor(progressPercent),
                  minWidth: '100px'
                }}>
                  Progress: {progressPercent}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progressPercent}
                  onChange={handleProgressChange}
                  disabled={isLocked}
                  style={{
                    flex: 1,
                    height: '12px',
                    borderRadius: '6px',
                    background: `linear-gradient(to right, ${getProgressColor(progressPercent)} 0%, ${getProgressColor(progressPercent)} ${progressPercent}%, #e9ecef ${progressPercent}%, #e9ecef 100%)`,
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          </div>

          {showConfirmDialog && (
            <Modal small onClose={cancelCompletion}>
              <ModalTitle>Confirm Subtask Completion</ModalTitle>
              <ModalContent>
                <p>Are you sure this subtask is completed? This action cannot be undone.</p>
              </ModalContent>
              <ModalActions>
                <button onClick={cancelCompletion}
                        style={{backgroundColor: '#ff9800',
                                borderRadius: '6px', 
                                marginRight: '10px',
                                border: 'none',
                                color: 'white'
                             }}>
                          Cancel
                </button>
                <Button primary onClick={confirmCompletion}>Confirm Completion</Button>
              </ModalActions>
            </Modal>
          )}
        </>
      );
  });

  const WeekChipSelector = ({ weeks, selectedWeek, onWeekSelect, existingTasks }) => {
      const isPastWeek = (weekDate) => {
        if (!weekDate) return false;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const targetWeek = new Date(weekDate);
        const weekEndDate = new Date(targetWeek);
        weekEndDate.setDate(weekEndDate.getDate() + 1);
        return weekEndDate < today;
      };

      // Sort weeks in descending order (most recent first)
      const sortedWeeks = [...weeks].sort((a, b) => new Date(b) - new Date(a));

      return (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
            Select Week to View/Edit
          </label>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '10px',
            maxHeight: '200px',
            overflowY: 'auto',
            padding: '8px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9'
          }}>
            {sortedWeeks.map((week) => {
              const isPast = isPastWeek(week);
              const hasIncompleteTasks = existingTasks.some(t => 
                t.selectedWeek === week && t.progressPercent < 100
              );
              const isSelected = selectedWeek === week;
              
              let statusColor = '';
              let statusMessage = '';
              
              if (isPast && hasIncompleteTasks) {
                statusColor = '#ff9800';
                statusMessage = '⚠️ Incomplete';
              } else if (isPast) {
                statusColor = '#9e9e9e';
                statusMessage = '🔒 Complete';
              } else {
                statusColor = '#4caf50';
                statusMessage = 'Current Week';
              }
              
              return (
                <div
                  key={week}
                  onClick={() => onWeekSelect(week)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    backgroundColor: isSelected ? statusColor : '#ffffff',
                    color: isSelected ? '#ffffff' : '#333333',
                    border: `2px solid ${statusColor}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '12px',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = statusColor;
                      e.currentTarget.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.color = '#333333';
                    }
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', opacity: 0.8 }}>Week ending</span>
                    <span style={{ fontWeight: 'bold' }}>{week}</span>
                    <span style={{ fontSize: '9px', marginTop: '2px' }}>{statusMessage}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {weeks.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              No weeks available
            </div>
          )}
        </div>
      );
 };


function WeeklyProgressTracker({ onSubmit, onCancel }) {
  const engine = useDataEngine();
  const [currentUser, setCurrentUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [existingTasks, setExistingTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSubmittedTaskThisWeek, setHasSubmittedTaskThisWeek] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [objectiveTasks, setObjectiveTasks] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [objectiveDrafts, setObjectiveDrafts] = useState({});
  const [kpis, setKpis] = useState([]);
  const [showKPISelection, setShowKPISelection] = useState(false);

  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);
  const [previousChallenges, setPreviousChallenges] = useState('');
  const [challengeNotificationSent, setChallengeNotificationSent] = useState(false);

  const [kpiEnforcementActive, setKpiEnforcementActive] = useState(false);

  const challengeNotificationSentRef = useRef(false);
  const previousChallengesRef = useRef('');
  const emailInProgressRef = useRef(false);

  const navigate = useNavigate();

  const [allWeeks, setAllWeeks] = useState([]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const autoSaveTimeoutRef = useRef(null);
  const notificationRef = useRef(null);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const [currentStep, setCurrentStep] = useState('SELECT_OBJECTIVE'); // 'SELECT_OBJECTIVE' | 'SELECT_KPI' | 'FILL_TASK'
  const [selectedKPI, setSelectedKPI] = useState(null);

  // Week ending state (shared by all tasks)
  const [selectedWeek, setWeekEnding] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    return sunday.toISOString().substring(0, 10);
  });

  // Multiple tasks state
  const [tasks, setTasks] = useState([{
    id: Date.now().toString(),
    taskName: "",
    taskDescription: "",
    progressPercent: 0,
    status: "",
    achievements: "",
    challenges: "",
    nextSteps: "",
    kpiId: "",
    kpiName: "",
    isNew: true,
    eventId: "", // Track backend event ID
    subTasks: [] // Array of subtasks
  }]);

  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const currentTask = tasks[selectedTaskIndex];
  const currentUserOrgUnitId = currentUser?.organisationUnits?.[0]?.id;

  
  const filteredObjectives = objectives.filter(obj =>
    obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.orgUnitName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const dateUtils = {
    // Safe date comparison that handles timezone and edge cases
    isDateAfter: (date1, date2) => {
      // Convert both to ISO strings and compare dates only (no time)
      const date1Str = new Date(date1).toISOString().split('T')[0];
      const date2Str = new Date(date2).toISOString().split('T')[0];

      return date1Str > date2Str;
    },

    // Check if we're on the same day
    isSameDay: (date1, date2) => {
      const date1Str = new Date(date1).toISOString().split('T')[0];
      const date2Str = new Date(date2).toISOString().split('T')[0];

      return date1Str === date2Str;
    },

    // Get end of day for a date
    getEndOfDay: (date) => {
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay;
    }
  };

  const hasMeaningfulContent = useCallback((objectiveId, tasksList) => {
      // Check if any task for this objective has activities/subtasks
      return tasksList.some(task => 
        task.subTasks && task.subTasks.length > 0 &&
        task.subTasks.some(sub => 
          sub.description?.trim() && sub.description.trim().length > 5
        )
      );
  }, []);

  const getObjectiveStatus = (objectiveId, hasSubmittedTask, tasksList) => {
      const hasContent = hasMeaningfulContent(objectiveId, tasksList);
      
      if (hasSubmittedTask) {
        return { type: 'submitted', label: '✓ SUBMITTED', color: '#28a745' };
      }
      if (hasContent) {
        return { type: 'draft', label: '✎ TOUCHED', color: '#17a2b8' };
      }
      return { type: 'untouched', label: '⚠ NOT TOUCHED', color: '#ffc107' };
  };

  const canUpdatePastWeekTask = (task) => {
    // Task can be updated if it's NOT 100% complete
    // This allows completing tasks that were started in past weeks
    return task && task.progressPercent < 100;
  };

  // Modified isWeekLocked function - only lock COMPLETED tasks, not entire weeks
  const isTaskLocked = (task, weekEndingDate) => {
    // Task is locked only if it's 100% complete
    // This allows editing incomplete tasks even in past weeks
    return task?.progressPercent === 100;
  };

    useEffect(() => {
      if (Object.keys(objectiveDrafts).length > 0) {
        try {
          localStorage.setItem('touchedObjectives', JSON.stringify(objectiveDrafts));
          console.log('💾 Saved touched status to localStorage:', objectiveDrafts);
        } catch (e) {
          console.warn('Failed to save touched status:', e);
        }
      }
    }, [objectiveDrafts]);

  
    // ============================================================================
    // TOUCHED STATUS MANAGEMENT - CONSOLIDATED
    // ============================================================================

    // 1. Initialize touched status from localStorage on mount
    useEffect(() => {
      try {
        const savedStatus = localStorage.getItem('touchedObjectives');
        if (savedStatus) {
          const parsedStatus = JSON.parse(savedStatus);
          setObjectiveDrafts(parsedStatus);
          console.log('📂 Loaded touched status from localStorage:', parsedStatus);
        }
      } catch (e) {
        console.warn('Failed to load touched status:', e);
      }
    }, []);

    // 2. Save touched status to localStorage whenever it changes
    useEffect(() => {
      if (Object.keys(objectiveDrafts).length > 0) {
        try {
          localStorage.setItem('touchedObjectives', JSON.stringify(objectiveDrafts));
          console.log('💾 Saved touched status to localStorage:', objectiveDrafts);
        } catch (e) {
          console.warn('Failed to save touched status:', e);
        }
      }
  }, [objectiveDrafts]);
    
  useEffect(() => {
    try {
      const storedCalendar = localStorage.getItem('company_financial_calendar');
      if (storedCalendar) {
        const parsedCalendar = JSON.parse(storedCalendar);
        if (parsedCalendar?.financialYearStart && parsedCalendar?.financialYearEnd) {
          const resolvedFiscalYear = resolveActiveFiscalYear({
            start: parsedCalendar.financialYearStart,
            end: parsedCalendar.financialYearEnd,
          });
          setFiscalYear(resolvedFiscalYear);
          setFiscalYearLoaded(true);
          return;
        }
      }
    } catch (error) {
      console.warn('Failed to load fiscal year from localStorage:', error);
    }

    setFiscalYear(getCurrentCalendarYearRange());
    setFiscalYearLoaded(true);
  }, []);

  const fetchStatusOptions = async () => {
    try {
      const response = await engine.query({
        optionSet: {
          resource: 'optionSets/U8X3RbnJrYs',
          params: { fields: 'options[code,name]' }
        }
      });

      console.log('Valid status options:', response.optionSet.options);
      return response.optionSet.options;
    } catch (error) {
      console.error('Error fetching status options:', error);
      return [];
    }
  };

  const handleBackToObjective = () => {
    // Reset all task-related state
    setCurrentStep('SELECT_OBJECTIVE');
    setSelectedKPI(null);
    setTasks([{
        id: Date.now().toString(),
        taskName: "",
        taskDescription: "",
        progressPercent: 0,
        status: "",
        achievements: "",
        challenges: "",
        nextSteps: "",
        kpiId: "",
        kpiName: "",
        isNew: true,
        subTasks: []
    }]);
    setSelectedTaskIndex(0);
    setHasUnsavedChanges(false);
    setLastAutoSaved(null);
    setKpiEnforcementActive(false);
    
    // ✅ Reset week selection to current week
    const today = new Date();
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    setWeekEnding(sunday.toISOString().substring(0, 10));
    
    // ✅ Clear the selected objective to force fresh selection
    // This will trigger the week selector to show all available weeks again
    setSelectedObjective(null);
    setHasSubmittedTaskThisWeek(false);
};


  const getAllWeeks = useCallback(() => {
    const weeks = new Set();

    // ✅ ALWAYS add current week
    weeks.add(selectedWeek);

    // Add all weeks from existing tasks
    existingTasks.forEach(task => {
      if (task.selectedWeek) {
        weeks.add(task.selectedWeek);
      }
    });

    // ✅ DEBUG: Log what weeks we found
    console.log('📊 getAllWeeks result:', {
      currentWeek: selectedWeek,
      existingTasksCount: existingTasks.length,
      totalWeeks: Array.from(weeks).length,
      weeks: Array.from(weeks)
    });

    // Sort weeks in descending order (most recent first)
    return Array.from(weeks).sort((a, b) => new Date(b) - new Date(a));
  }, [existingTasks, selectedWeek]);

  // Update allWeeks when existing tasks change
  useEffect(() => {
    const weeks = getAllWeeks();
    setAllWeeks(weeks);
  }, [getAllWeeks]);

  // Auto-show form when NO existing tasks exist (new week)
    useEffect(() => {
      if (!hasSubmittedTaskThisWeek) {
        setShowTaskForm(true);
      }
    }, [hasSubmittedTaskThisWeek]);

  // Function to check if a specific week is locked
  const isSpecificWeekLocked = (weekDate) => {
    const now = new Date();
    const targetWeek = new Date(weekDate);

    // Add 7 days grace period
    const lockDate = new Date(targetWeek);
    lockDate.setDate(lockDate.getDate() + 7);

    return now > lockDate;
  };

  const checkObjectiveHasDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      const response = await engine.query({
        draft: {
          resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        }
      });
      return !!response.draft;
    } catch (error) {
      return false;
    }
  };


  const fetchKPIsForObjective = async (objectiveId) => {
    try {
      // Fetch KPIs that are linked to the same objective
      const response = await engine.query({
        kpis: {
          resource: 'events',
          params: {
            program: KPI_PROGRAM_ID,
            programStage: KPI_PROGRAM_STAGE_ID,
            filter: [`plJovUlxfQ0:eq:${objectiveId}`],
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      });

      console.log('Raw KPI response:', response);

      // Process KPI data
      const processedKPIs = response.kpis?.events?.map(event => {
        const dataValues = event.dataValues || [];

        return {
          id: event.event, // Use event ID as KPI identifier
          eventId: event.event,
          name: dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI',
          description: dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '',
          target: dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || 'N/A',
          objectiveId: dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '',
          orgUnitName: event.orgUnitName,
          eventDate: event.eventDate
        };
      }) || [];

      console.log('Processed KPIs:', processedKPIs);
      setKpis(processedKPIs);

      return processedKPIs;
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      showNotification('Failed to load KPIs for this objective', 'error');
      setKpis([]);
      return [];
    }
  };


  const handleKPISelect = (kpi) => {
    console.log('🎯 KPI Selected - Before update:', {
      taskName: currentTask.taskName,
      hasSubmittedTaskThisWeek: hasSubmittedTaskThisWeek,
      willAutoSave: !!selectedObjective
    });
    updateCurrentTask({
      kpiId: kpi.eventId,
      kpiName: kpi.name
    });

    // Disable KPI enforcement once KPI is selected
    if (kpiEnforcementActive) {
      setKpiEnforcementActive(false);
      showNotification("KPI linked successfully! You can now fill other task details.", "success");
    }
  };

  const shouldDisableField = () => {
    return kpiEnforcementActive && !currentTask.kpiId;
  };

  const handleKPIChange = (kpi) => {
    if (kpi) {
      handleKPISelect(kpi);
    } else {
      updateCurrentTask({
        kpiId: "",
        kpiName: ""
      });
    };
  };


  // Enhanced notification system
  const showNotification = (message, type = 'error') => {
    if (window.notificationTimeout) {
      clearTimeout(window.notificationTimeout);
    }

    notificationRef.current = { message, type, timestamp: Date.now() };
    setNotification({ message, type });

    window.notificationTimeout = setTimeout(() => {
      if (notificationRef.current?.message === message) {
        setNotification(null);
        notificationRef.current = null;
      }
      window.notificationTimeout = null;
    }, 5000);
  };


// ============================================================================
// SEND CHALLENGE NOTIFICATION TO REPORTING MANAGER
// ============================================================================
const sendChallengeNotification = async (task, objective, challenges) => {
  
  // ✅ NEW: Task-aware duplicate check with content hash
  const emailKey = getChallengeEmailKey(task, selectedWeek, challenges);
  const emailCheck = shouldSendChallengeEmail(emailKey);
  
  if (!emailCheck.shouldSend) {
    const remainingMin = Math.ceil(emailCheck.remainingMs / 60000);
    console.log('🔄 [EMAIL] Cooldown active, skipping', {
      reason: emailCheck.reason,
      remainingMinutes: remainingMin,
      lastSent: emailCheck.lastSent ? new Date(emailCheck.lastSent).toLocaleTimeString() : null
    });
    return { 
      success: true, 
      message: `Notification sent recently, retry in ${remainingMin}m`, 
      duplicate: true,
      cooldownRemaining: emailCheck.remainingMs
    };
  }
  
  console.log('🔍 [CHALLENGE EMAIL] Starting challenge notification', {
    taskName: task?.taskName,
    week: selectedWeek,
    challengesLength: challenges?.length,
    emailKey,
    checkReason: emailCheck.reason
  });

  try {
    let recipient = null;
    const currentUserName = currentUser?.displayName || currentUser?.username || 'Unknown User';

    // ====== STRATEGY: Get manager from current user's position ======
    if (currentUserPosition?.attributes) {
      const attributes = {};
      currentUserPosition.attributes?.forEach(attr => {
        attributes[attr.attribute] = attr.value;
      });
      
      const reportingToTei = attributes['SjP47qi3JeB'];
      
      if (reportingToTei) {
        console.log('🔍 [CHALLENGE EMAIL] Found reportingTo TEI:', reportingToTei);
        
        try {
          const positionResponse = await engine.query({
            reportingPosition: {
              resource: `trackedEntityInstances/${reportingToTei}`,
              params: { fields: 'trackedEntityInstance,attributes[attribute,value]' }
            }
          });
          
          if (positionResponse.reportingPosition) {
            const positionAttrs = {};
            positionResponse.reportingPosition.attributes?.forEach(attr => {
              positionAttrs[attr.attribute] = attr.value;
            });
            
            const incumbentId = positionAttrs['V66IFsw5kq6'];
            
            if (incumbentId) {
              // Try User UID first, then fallback to Staff TEI
              try {
                const userResponse = await engine.query({
                  managerUser: {
                    resource: `users/${incumbentId}`,
                    params: { fields: 'id,displayName,email,userCredentials[username]' }
                  }
                });
                if (userResponse.managerUser) {
                  recipient = userResponse.managerUser;
                  console.log('✅ [EMAIL] Found manager via User UID:', recipient.displayName);
                }
              } catch (uidError) {
                // Fallback: Try as Staff TEI
                const staffResponse = await engine.query({
                  staff: {
                    resource: `trackedEntityInstances/${incumbentId}`,
                    params: { fields: 'trackedEntityInstance,attributes[attribute,value]' }
                  }
                });
                const staffAttrs = {};
                staffResponse.staff?.attributes?.forEach(attr => {
                  staffAttrs[attr.attribute] = attr.value;
                });
                const staffUsername = staffAttrs['g3RRIfDiQra'];
                if (staffUsername) {
                  const userResponse = await engine.query({
                    users: {
                      resource: 'users',
                      params: {
                        filter: `username:eq:${staffUsername}`,
                        fields: 'id,displayName,email,userCredentials[username]'
                      }
                    }
                  });
                  recipient = userResponse.users?.users?.[0];
                  if (recipient) {
                    console.log('✅ [EMAIL] Found manager via Staff TEI:', recipient.displayName);
                  }
                }
              }
            }
          }
        } catch (hierarchyError) {
          console.warn('⚠️ [EMAIL] Position hierarchy lookup failed:', hierarchyError);
        }
      }
    }

    // ====== VALIDATION ======
    if (!recipient || !recipient.email) {
      console.warn('⚠️ [EMAIL] No recipient found with email');
      return { success: false, error: 'No recipient with email found' };
    }

    // ✅ Safe name resolution with fallbacks
    const recipientName = recipient.displayName || 
                          recipient.name || 
                          `${recipient.firstName || ''} ${recipient.surname || ''}`.trim() ||
                          recipient.email?.split('@')[0] ||
                          'Manager';

    console.log('✅ [EMAIL] FINAL RECIPIENT:', {
      name: recipientName,
      email: recipient.email,
      id: recipient.id
    });

    // ====== SEND EMAIL ======
    const emailData = {
      to: recipient.email,
      subject: `⚠️ Task Challenge: ${task?.taskName}`,
      message: `
Dear ${recipientName},

${currentUserName} has reported challenges while working on their weekly task.

**Task Details:**
- Task Name: ${task?.taskName}
- Week Ending: ${selectedWeek}
- Objective: ${objective?.name || 'N/A'}
- Progress: ${task?.progressPercent}%

**Challenges Reported:**
${challenges || 'No specific challenges detailed'}

**Additional Context:**
- Achievements: ${task?.achievements || 'N/A'}
- Next Steps: ${task?.nextSteps || 'N/A'}

Please see if you can provide support if needed.

Best regards,
Performance Management System
`.trim()
    };

    try {
      console.log('📧 [EMAIL] Sending message...');
      const result = await engine.mutate({
        resource: 'messageConversations',
        type: 'create',
        data: {
          subject: emailData.subject,
          text: emailData.message,
          users: [{ id: recipient.id }],
          organisationUnits: [],
          userGroups: []
        }
      });
      
      console.log('✅ [EMAIL] Sent successfully to:', recipientName);
      
      // ✅ NEW: Mark as sent with cooldown (15 minutes = 900000ms)
      markChallengeEmailSent(emailKey, 15 * 60 * 1000);
      
      return {
        success: true,
        message: 'Email sent successfully',
        recipientName: recipientName,
        recipientEmail: recipient.email,
        response: result
      };
    } catch (apiError) {
      console.warn('⚠️ [EMAIL] API failed:', apiError);
      return {
        success: false,
        error: 'Email API failed',
        recipientName: recipientName,
        recipientEmail: recipient.email,
        warning: `Please notify ${recipientName} manually.`
      };
    }
  } catch (error) {
    console.error('❌ [EMAIL] Critical error:', error);
    return {
      success: false,
      error: error.message,
      warning: 'Email notification failed'
    };
  }
};

  // Update the isWeekLocked function
  const isWeekLocked = (weekEndingDate, task) => {
    try {
      const now = new Date();
      const selectedWeek = new Date(weekEndingDate);
      const lockDate = new Date(selectedWeek);
      lockDate.setDate(lockDate.getDate() + 7); // Allow editing for 7 days

      // Return true if past the lock date OR task is 100% complete
      return now > lockDate || task?.progressPercent === 100;
    } catch (error) {
      console.error('Error in week locking logic:', error);
      return false;
    }
  };


  // Update this to allow editing existing tasks in past weeks
  // Disable form only when task is 100% complete
  // Check if form should be disabled for this specific task
  const isFormDisabled = useCallback(() => {
      // For new tasks (no eventId and progressPercent is 0), form should be enabled
      if (!currentTask?.eventId && currentTask?.progressPercent === 0) {
        return false;
      }
      // This allows completion of tasks from any week
      return currentTask?.progressPercent === 100 && currentTask?.eventId;
  }, [currentTask?.progressPercent, currentTask?.eventId]);

  // Allow editing existing tasks even in past weeks
  const canEditTask = !isFormDisabled;

  // Check if user can add a new task (only in current week, not past weeks)
  const canAddNewTaskForWeek = !isViewOnlyWeek(selectedWeek) && !hasSubmittedTaskThisWeek;

  // Check if we're viewing a past week with existing tasks
  const isViewingPastWeekWithExistingTask = isViewOnlyWeek(selectedWeek) && hasSubmittedTaskThisWeek;

  // Generate draft ID for dataStore
  const getDraftId = (objectiveId, selectedWeek) => {
    return `${DRAFT_NAMESPACE}-${objectiveId}-${selectedWeek}`;
  };

  const syncTasksToDatabase = async (tasksData, objective, selectedWeek) => {
    try {
      // ✅ NEW: Filter out tasks with invalid names BEFORE syncing
      const validTasks = tasksData.filter(task =>
        task.taskName && task.taskName.trim() !== '' && task.taskName !== 'Unnamed Task'
      );

      if (validTasks.length === 0) {
        console.log('⏸️ No valid tasks to sync to database');
        return { successCount: 0, errorCount: 0 };
      }

      // Get existing events for this objective and week to avoid duplicates
      const existingEvents = await getExistingEvents(objective.id, selectedWeek);
      console.log('📋 Existing events for update:', existingEvents.map(e => ({ id: e.event, dataValues: e.dataValues })));

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process each VALID task
      for (const task of validTasks) {
        console.log('🔍 Syncing valid task:', {
          taskName: task.taskName,
          hasKpiId: !!task.kpiId,
          hasStatus: !!task.status,
          progress: task.progressPercent
        });

        try {
          await createOrUpdateTaskEvent(task, objective, selectedWeek, existingEvents);
          successCount++;
          console.log(`✅ Synced task: "${task.taskName}"`);
        } catch (error) {
          console.error(`❌ Failed to sync task "${task.taskName}":`, error);
          errorCount++;
          errors.push(error.message);
        }
      }

      console.log(`📊 Sync Results: ${successCount} successful, ${errorCount} failed`);

      if (errorCount > 0) {
        console.warn(`Some tasks failed to sync: ${errors.join('; ')}`);
      }

      // Verify creation if we had any successes
      if (successCount > 0) {
        try {
          await verifyTaskCreation(objective.id, validTasks[0].taskName);
        } catch (verifyError) {
          console.warn('Verification failed but continuing:', verifyError);
        }
      }

      console.log('✅ Auto-save completed - tasks synced to the database');
      return { successCount, errorCount };

    } catch (error) {
      console.error('❌ Error syncing tasks to database:', error);
      return { successCount: 0, errorCount: tasksData.length };
    }
  };


  const createOrUpdateTaskEvent = async (task, objective, selectedWeek, existingEvents) => {
      const objectivePositionTei = objective.trackedEntityInstance;

      try {
        // Prepare data values
        const dataValues = prepareDataValues(task, objective, selectedWeek, objectivePositionTei);

        // Check if event exists (by task name and objective)
        const existingEvent = findExistingEvent(task, existingEvents);

        if (existingEvent) {
          // UPDATE existing event - ALWAYS allow updates even for past weeks
          console.log(`🔄 Updating existing task from ${selectedWeek}: "${task.taskName}"`);
          return await updateExistingEvent(existingEvent.event, dataValues, task);
        } else {
          // For NEW tasks: only allow creation for current week OR if task is 100% complete
          const isCurrentWeek = !isPastWeek(selectedWeek);
          const isTaskComplete = task.progressPercent === 100;
          
          if (isCurrentWeek || isTaskComplete) {
            console.log(`✨ Creating new task for ${selectedWeek}: "${task.taskName}"`);
            return await createNewEvent(objective, dataValues, objectivePositionTei, task);
          } else {
            console.log(`⏸️ Skipping creation for past week task (not complete): "${task.taskName}"`);
            return { success: false, action: 'skipped', reason: 'past_week_incomplete' };
          }
        }

      } catch (error) {
        handleTaskError(error, task);
        throw error;
      }
   };


  // Helper function 1: Prepare data values
  const prepareDataValues = (task, objective, selectedWeek, objectivePositionTei) => {
    const dataValues = [
      { dataElement: TASK_DATAELEMENT_MAPPING.selectedWeek, value: selectedWeek },
      { dataElement: TASK_DATAELEMENT_MAPPING.progressPercent, value: task.progressPercent?.toString() || '0' },
      { dataElement: TASK_DATAELEMENT_MAPPING.status, value: task.status || 'pending' },
      { dataElement: TASK_DATAELEMENT_MAPPING.achievements, value: task.achievements || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.challenges, value: task.challenges || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.nextSteps, value: task.nextSteps || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.objectiveId, value: objective.id },
      { dataElement: TASK_DATAELEMENT_MAPPING.taskName, value: task.taskName || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.taskDescription, value: task.taskDescription || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.kpiId, value: task.kpiId || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.positionTei, value: objectivePositionTei },
      { dataElement: TASK_DATAELEMENT_MAPPING.subTasks, value: JSON.stringify(task.subTasks || []) }
    ].filter(dv => dv.value !== undefined && dv.value !== null);

    console.log('🔍 Task data prepared:', {
      taskName: task.taskName || 'Unnamed Task',
      dataValuesCount: dataValues.length,
      hasKPI: !!task.kpiId,
      subTasksCount: task.subTasks?.length || 0
    });

    return dataValues;
  };


   // Helper function 2: Find existing event - IMPROVED
   const findExistingEvent = (task, existingEvents) => {
      // First try to find by event ID if it exists
      if (task.eventId) {
        const eventById = existingEvents.find(event => event.event === task.eventId);
        if (eventById) {
          console.log('🔄 Found existing event by ID:', eventById.event);
          return eventById;
        }
      }
      
      // Then try by task name
      const existingEvent = existingEvents.find(event => {
        const taskNameValue = event.dataValues?.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value;
        return taskNameValue === (task.taskName || '');
      });

      if (existingEvent) {
        console.log('🔄 Found existing event by name:', existingEvent.event);
      }

      return existingEvent;
  };

  // Helper function 3: Update existing event
  const updateExistingEvent = async (eventId, dataValues, task) => {
    try {
      console.log('🔄 Attempting to update event:', eventId);
      console.log('📤 Update dataValues:', dataValues);

      const result = await engine.mutate({
        resource: `events/${eventId}`,
        type: 'update',
        data: {
          program: PROGRAM_ID,
          programStage: TASK_PROGRAM_STAGE_ID,
          orgUnit: currentUserOrgUnitId,
          dataValues
        }
      });

      console.log(`✅ Successfully updated event for task: "${task.taskName || 'Unnamed Task'}"`);
      return { success: true, action: 'updated', eventId };

    } catch (error) {
      console.error(`❌ Failed to update event ${eventId} for task: "${task.taskName || 'Unnamed Task'}"`);

      // Enhanced error logging for updates
      if (error.details) {
        console.error('❌ Update error details:', {
          message: error.message,
          httpStatus: error.httpStatus,
          httpStatusCode: error.httpStatusCode
        });

        if (error.details.response) {
          console.error('❌ Update response:', error.details.response);

          if (error.details.response.importSummaries) {
            const importSummary = error.details.response.importSummaries[0];
            console.error('❌ Update import summary:', {
              status: importSummary?.status,
              description: importSummary?.description,
              conflicts: importSummary?.conflicts
            });
          }
        }
      }

      throw new Error(`Failed to update task "${task.taskName || 'Unnamed Task'}": ${error.message}`);
    }
  };

  // Helper function 4: Create new event
  const createNewEvent = async (objective, dataValues, objectivePositionTei, task) => {
    console.log('🎯 Creating task for objective:', objective.id, 'with position TEI:', objective.trackedEntityInstance);
    try {

      const enrollment = objective.enrollment
      const eventData = {
        program: PROGRAM_ID,
        programStage: TASK_PROGRAM_STAGE_ID,
        orgUnit: currentUserOrgUnitId || objective.orgUnit,
        eventDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        trackedEntityInstance: objectivePositionTei,
        enrollment: enrollment,
        dataValues: dataValues
      };

      console.log('🚀 Creating new event with payload:', {
        orgUnit: currentUserOrgUnitId || objective.orgUnit,
        trackedEntityInstance: objectivePositionTei,
        dataValuesCount: dataValues.length
      });

      const result = await engine.mutate({
        resource: 'events',
        type: 'create',
        data: eventData
      });

      console.log(`✅ Successfully created event for task: "${task.taskName || 'Unnamed Task'}"`);
      return { success: true, action: 'created', result };

    } catch (error) {
      console.error(`❌ Failed to create event for task: "${task.taskName || 'Unnamed Task'}"`);

      // Handle warnings as success (events that are created but with warnings)
      if (error.details?.response?.importSummaries?.[0]?.importCount?.imported === 1) {
        console.log(`⚠️ Event created with warnings for task: "${task.taskName || 'Unnamed Task'}"`);
        return { success: true, action: 'created_with_warnings', warnings: true };
      }

      // Enhanced error logging for creation
      if (error.details?.response) {
        const importSummary = error.details.response.importSummaries?.[0];
        console.error('❌ Creation error details:', {
          imported: importSummary?.importCount?.imported,
          updated: importSummary?.importCount?.updated,
          status: importSummary?.status,
          description: importSummary?.description,
          conflicts: importSummary?.conflicts
        });
      }

      throw new Error(`Failed to create task "${task.taskName || 'Unnamed Task'}": ${error.message}`);
    }
  };

  // Helper function 5: Error handler
  const handleTaskError = (error, task) => {
    console.error(`💥 Critical error for task: "${task.taskName || 'Unnamed Task'}"`, {
      errorMessage: error.message,
      taskName: task.taskName,
      hasKPI: !!task.kpiId,
      progress: task.progressPercent
    });
  };

  // Get existing events for this objective and week
  const getExistingEvents = async (objectiveId, selectedWeek) => {
    try {
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            filter: [
              `plJovUlxfQ0:eq:${objectiveId}`,
              `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${selectedWeek}`
            ],
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 100
          }
        }
      });

      return response.events?.events || [];
    } catch (error) {
      console.error('Error fetching existing events:', error);
      return [];
    }
  };

  const saveDraft = async (tasksData, objective, selectedWeek) => {
      const allTasksComplete = tasksData.every(task => task.progressPercent === 100);
      
      if (!objective) {
        console.log('Skipping auto-save: No objective');
        return;
      }

      const validTasks = tasksData.filter(task =>
        task.taskName && task.taskName.trim() !== '' && task.taskName !== 'Unnamed Task'
      );

      if (allTasksComplete && validTasks.length === 0) {
        console.log('Skipping final save: No task with a valid name');
        return;
      }

      if (validTasks.length === 0) {
        console.log('⏸️ Skipping auto-save: No task with a valid name');
        return;
      }

      try {
        setIsAutoSaving(true);

        if (!allTasksComplete) {
          const draftId = getDraftId(objective.id, selectedWeek);
          const draftData = {
            tasks: validTasks, 
            objective,
            lastSaved: new Date().toISOString(),
            selectedWeek
          };

          await engine.mutate({
            resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
            type: 'update',
            data: draftData,
          });
        }

        const result = await syncTasksToDatabase(validTasks, objective, selectedWeek); 

        if (allTasksComplete) {
          await deleteDraft(objective.id, selectedWeek);
          console.log('Final completion synced to database and draft cleared');
        }

        setLastAutoSaved(new Date());
        setHasUnsavedChanges(false);

        if (objective && result.successCount > 0) {
        // Force update touched status immediately after successful save
        const hasAnyContentAfterSave = validTasks.some(task => {
          const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task' && task.taskName !== '';
          const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
          const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
          const hasChallenges = task.challenges?.trim() && task.challenges.trim().length > 5;
          const hasActivities = task.subTasks && task.subTasks.length > 0;
          const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;
          
          return hasValidTaskName || hasTaskDescription || hasAchievements || hasChallenges || hasActivities || hasProgress;
        });
        
        console.log(`🔍 [SAVE] Force updating touched status for ${objective.name}: ${hasAnyContentAfterSave ? 'TOUCHED ✅' : 'NOT TOUCHED ⚠️'}`);
        
        setObjectiveDrafts(prev => ({
          ...prev,
          [objective.id]: hasAnyContentAfterSave
        }));
      }

        // Force refresh of objective drafts status - SET TO TRUE if any content exists
        const hasAnyContent = validTasks.some(task => {
          const hasActivities = task.subTasks?.length > 0;
          const hasProgress = task.progressPercent > 0;
          const hasAchievements = task.achievements?.trim()?.length > 0;
          const hasChallenges = task.challenges?.trim()?.length > 0;
          const hasTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task' && task.taskName !== '';
          return hasActivities || hasProgress || hasAchievements || hasChallenges || hasTaskName;
        });

        // Immediately update touched status after successful save
          if (hasAnyContent && objective) {
            setObjectiveDrafts(prev => ({
              ...prev,
              [objective.id]: true
            }));
          }
       
        const incompleteCount = validTasks.filter(t => t.progressPercent < 100).length;
        const completeCount = validTasks.filter(t => t.progressPercent === 100).length;
        
        if (result.successCount > 0) {
          const weekType = isPastWeek(selectedWeek) ? 'past week' : 'current week';
          showNotification(
            `✓ Saved: ${result.successCount} task(s) updated in ${weekType}. ` +
            `${incompleteCount} in progress, ${completeCount} complete.`, 
            'success'
          );
        } else if (validTasks.length > 0 && result.errorCount === 0) {
          showNotification('✅ Draft saved. Add a task name to sync to database.', 'success');
        } else if (result.errorCount > 0) {
          showNotification(`⚠️ ${result.errorCount} task(s) failed to sync. Progress saved to draft.`, 'warning');
        } else {
          showNotification('Progress saved to draft.', 'success');
        }

      } catch (error) {
        console.error('❌ Auto-save failed:', error);
        showNotification('Failed to save progress', 'error');
      } finally {
        setIsAutoSaving(false);
      }
  };

  // Updated   loadDraft to handle multiple tasks
  const loadDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      const response = await engine.query({
        draft: {
          resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        }
      });

      if (response.draft && response.draft.tasks && Array.isArray(response.draft.tasks)) {
        // Validate that tasks have the expected structure
        const validTasks = response.draft.tasks.map(task => ({
          id: task.id || Date.now().toString(),
          taskName: task.taskName || "",
          taskDescription: task.taskDescription || "",
          progressPercent: task.progressPercent || 0,
          status: task.status || "",
          achievements: task.achievements || "",
          challenges: task.challenges || "",
          nextSteps: task.nextSteps || "",
          kpiId: task.kpiId || "",
          kpiName: task.kpiName || "",
          isNew: task.isNew !== undefined ? task.isNew : true,
          eventId: task.eventId || "",
          subTasks: task.subTasks || []
        }));

        setTasks(validTasks);
        setLastAutoSaved(new Date(response.draft.lastSaved));
        setHasUnsavedChanges(false);
        showNotification('Loaded auto-saved tasks from your previous session', 'success');
        return true;
      }
    } catch (error) {
      // ✅ EXPECTED: No draft exists yet, this is normal
      console.log('No draft found for this objective/week - this is normal');
    }
    return false;
  };

  // Delete draft (when form is submitted or cleared)
  const deleteDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      await engine.mutate({
        resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        type: 'delete',
      });
    } catch (error) {
      console.log('No draft to delete or delete failed');
    }
  };

  // Check if a task already exists for this week
  const checkExistingTaskThisWeek = async (objectiveId) => {
    try {
      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            filter: [
              `plJovUlxfQ0:eq:${objectiveId}`,
              `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${selectedWeek}`
            ],
            fields: 'event',
            pageSize: 1
          }
        }
      });
      return response.tasks?.events?.length > 0;
    } catch (error) {
      console.error('Error checking existing tasks:', error);
      return false;
    }
  };

   
    // ============================================================================
    // AUTO-SAVE WITH CHALLENGE EMAIL NOTIFICATION - FIXED VERSION
    // ============================================================================
    useEffect(() => {
      // Check if there's ANY meaningful content (not just valid task name)
      const hasAnyContent = tasks.some(task => {
        // Check for any field with content
        const hasTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task';
        const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 5;
        const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
        const hasChallenges = task.challenges?.trim() && task.challenges.trim().length > 5;
        const hasActivityContent = task.subTasks?.some(sub => 
          (sub.description?.trim() && sub.description.trim().length > 5) ||
          (sub.progressPercent > 0)
        );
        
        return hasTaskName || hasTaskDescription || hasAchievements || hasChallenges || hasActivityContent;
      });
      
      // Check if there are ANY incomplete tasks across all weeks
      const hasAnyIncompleteTask = tasks.some(task => task.progressPercent < 100);
      
      // Allow auto-save if there's ANY content OR unsaved changes
      const shouldAutoSave = 
        hasAnyContent && 
        hasUnsavedChanges && 
        selectedObjective && 
        hasAnyIncompleteTask;
      
      const allTasksComplete = tasks.length > 0 && tasks.every(task => task.progressPercent === 100);
      
      if (shouldAutoSave && !allTasksComplete) {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
        
        autoSaveTimeoutRef.current = setTimeout(async () => {
          console.log('✅ AUTOSAVE TRIGGERED:', {
            week: selectedWeek,
            tasksCount: tasks.length,
            incompleteTasks: tasks.filter(t => t.progressPercent < 100).length,
            isPastWeek: isPastWeek(selectedWeek)
          });
          
          try {
            await saveDraft(tasks, selectedObjective, selectedWeek);
            
            const currentChallengesNow = currentTask?.challenges || '';
            const hasContentNow = currentChallengesNow?.trim().length > 0;
            const challengesChangedNow = currentChallengesNow !== previousChallengesRef.current;
            const hasNewChallengesNow = hasContentNow && challengesChangedNow;
            
            if (hasNewChallengesNow && !challengeNotificationSentRef.current && !emailInProgressRef.current) {
              if (currentUserPosition?.trackedEntityInstance) {
                emailInProgressRef.current = true;
                try {
                  const result = await sendChallengeNotification(currentTask, selectedObjective, currentChallengesNow);
                  if (result.success) {
                    challengeNotificationSentRef.current = true;
                    const recipientName = result.recipientName || 'your manager';
                    showNotification(`Challenge notification sent to ${recipientName}`, 'success');
                  }
                } catch (error) {
                  console.error('❌ [EMAIL ERROR]:', error);
                } finally {
                  setTimeout(() => { emailInProgressRef.current = false; }, 2000);
                }
              }
            }
            previousChallengesRef.current = currentChallengesNow;
          } catch (error) {
            console.error('❌ Auto-save failed:', error);
          }
        }, AUTO_SAVE_DELAY);
        
        return () => {
          if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        };
      }
    }, [tasks, hasUnsavedChanges, selectedObjective, selectedWeek, currentTask?.challenges, currentTask?.progressPercent, selectedTaskIndex]);

    // Add this useEffect to detect when tasks are loaded from the database
    useEffect(() => {
      if (!selectedObjective) return;
      if (tasks.length === 0) return;
      
      // Check if any task has meaningful content
      const hasContent = tasks.some(task => {
        const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task' && task.taskName !== '';
        const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
        const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
        const hasChallenges = task.challenges?.trim() && task.challenges.trim().length > 5;
        const hasActivities = task.subTasks && task.subTasks.length > 0;
        const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;
        
        return hasValidTaskName || hasTaskDescription || hasAchievements || hasChallenges || hasActivities || hasProgress;
      });
      
      console.log(`🔍 [LOAD] Initial content check for ${selectedObjective.name}: ${hasContent ? 'HAS CONTENT ✅' : 'NO CONTENT ⚠️'}`);
      
      setObjectiveDrafts(prev => {
        const current = prev[selectedObjective.id] || false;
        if (current !== hasContent) {
          return { ...prev, [selectedObjective.id]: hasContent };
        }
        return prev;
      });
   }, [selectedObjective, tasks]); // This will run whenever tasks change
   
  // Calculate task progress from subtasks
  const calculateTaskProgress = (subTasks) => {
    if (!subTasks || subTasks.length === 0) return 0;
    const totalProgress = subTasks.reduce((sum, subTask) => sum + (subTask.progressPercent || 0), 0);
    return Math.round(totalProgress / subTasks.length);
  };


  const handleSearchChange = ({ value }) => {
    setSearchTerm(value);
  };



  const debugPositionTEIAssignment = (tasks, objective) => {
    console.log('🔍 POSITION TEI DEBUG - Task Creation:');
    console.log('Objective:', {
      id: objective.id,
      name: objective.name,
      trackedEntityInstance: objective.trackedEntityInstance,
      hasPositionTEI: !!objective.trackedEntityInstance
    });

    tasks.forEach((task, index) => {
      console.log(`Task ${index + 1}: "${task.taskName}"`, {
        willGetPositionTEI: objective.trackedEntityInstance,
        hasRequiredData: !!(task.taskName && task.kpiId && task.status)
      });
    });
  };

  // Add this debug function to verify task creation
  const verifyTaskCreation = async (objectiveId, taskName) => {
    try {
      const response = await fetch(`/api/events.json?program=yiT00XKrrHP&programStage=Nv9nYNcBTdM&filter=plJovUlxfQ0:eq:${objectiveId}&fields=event,dataValues[dataElement,value]`);
      const data = await response.json();

      const newTask = data.events?.find(event => {
        const taskNameValue = event.dataValues.find(dv => dv.dataElement === 'I0QtZkMbzxN')?.value;
        return taskNameValue === taskName;
      });

      if (newTask) {
        const positionTei = newTask.dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;
        console.log('✅ VERIFIED: New task created with Position TEI:', {
          taskName: taskName,
          positionTei: positionTei,
          hasPositionTei: !!positionTei
        });
      } else {
        console.log('❌ TASK NOT FOUND: Task may not have been created');
      }
    } catch (error) {
      console.error('Error verifying task creation:', error);
    }
  };


  // Update the objective selection
  const handleObjectiveSelect = async (objective) => {
    setSelectedObjective(objective);

    debugPositionTEIAssignment(tasks, objective);

    // Check for existing submitted task
    const alreadyExists = await checkExistingTaskThisWeek(objective.id);
    setHasSubmittedTaskThisWeek(alreadyExists);

    if (alreadyExists) {
      showNotification("This objective already has a task for the selected week. Viewing mode only.", "warning");
      await loadExistingTasksForWeek(objective.id);
      setKpiEnforcementActive(false);
    } 

    // Fetch KPIs for this objective
    await fetchKPIsForObjective(objective.id);

    // Update the draft status for this objective
    const hasDraft = !alreadyExists && await checkObjectiveHasDraft(objective.id, selectedWeek);
    setObjectiveDrafts(prev => ({
      ...prev,
      [objective.id]: hasDraft
    }));
  };


  // Load ALL tasks for a specific week
  const loadExistingTasksForWeek = async (objectiveId, weekDate = selectedWeek) => {
      try {
        console.log(`🔍 Loading tasks for objective ${objectiveId}, week ${weekDate}`);

        const response = await engine.query({
          tasks: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: TASK_PROGRAM_STAGE_ID,
              filter: [
                `plJovUlxfQ0:eq:${objectiveId}`,
                `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${weekDate}`
              ],
              fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
              pageSize: 1000,
              order: 'lastUpdated:desc'
            }
          }
        });

        if (response.tasks?.events?.length > 0) {
          console.log(`✅ Found ${response.tasks.events.length} task(s) for week ${weekDate}`);

          const tasksData = response.tasks.events.map(event => {
            const dataValues = event.dataValues || [];

            const subTasksValue = dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.subTasks)?.value;
            let subTasks = [];
            try {
              subTasks = subTasksValue ? JSON.parse(subTasksValue) : [];
            } catch (error) {
              console.warn('Failed to parse subtasks JSON:', error);
              subTasks = [];
            }

            const kpiId = dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '';
            const kpi = kpis.find(k => k.eventId === kpiId);

            return {
              id: event.event,
              eventId: event.event,
              taskName: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || 'Unnamed Task',
              taskDescription: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
              progressPercent: Number(dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0,
              status: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || '',
              achievements: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
              challenges: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
              nextSteps: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
              kpiId: kpiId,
              kpiName: kpi?.name || '',
              subTasks: subTasks,
              isNew: false,
              selectedWeek: weekDate
            };
          });

          let initialIndex = 0;
          const taskWithActivities = tasksData.findIndex(t => t.subTasks && t.subTasks.length > 0);
          if (taskWithActivities !== -1) {
            initialIndex = taskWithActivities;
            console.log(`✅ Auto-selected task ${initialIndex + 1} (has ${tasksData[initialIndex].subTasks.length} activities)`);
          } else {
            console.log('⚠️ No tasks have activities - showing first task');
          }

          setTasks(tasksData);

          if (tasksData.length > 0) {
            const hasContent = tasksData.some(task => 
              task.taskName?.trim() && task.taskName !== 'Unnamed Task' ||
              task.subTasks?.length > 0 ||
              task.progressPercent > 0 ||
              (task.achievements?.trim()?.length > 10) ||
              (task.challenges?.trim()?.length > 10)
            );
            
            if (hasContent) {
              setObjectiveDrafts(prev => {
                const updatedDrafts = { ...prev, [objectiveId]: true };
                localStorage.setItem('touchedObjectives', JSON.stringify(updatedDrafts));
                return updatedDrafts;
              });
            }
          }

          setSelectedTaskIndex(initialIndex);
          setHasSubmittedTaskThisWeek(true);
          setShowTaskForm(true);  // ✅ CHANGE: Set to true to show the form with loaded tasks
          showNotification(
            `Loaded ${tasksData.length} task(s) from ${weekDate}. Selected task with activities.`,
            'success'
          );

        } else {
          console.log(`ℹ️ No tasks found for week ${weekDate}, creating new task form`);

          // No tasks exist for this week - create new task
          setTasks([{
            id: Date.now().toString(),
            taskName: '',
            taskDescription: '',
            progressPercent: 0,
            status: '',
            achievements: '',
            challenges: '',
            nextSteps: '',
            kpiId: '',
            kpiName: '',
            isNew: true,
            subTasks: []
          }]);
          setSelectedTaskIndex(0);
          setHasSubmittedTaskThisWeek(false);
          setShowTaskForm(true);  // ✅ CHANGE: Set to true to show the blank form
          // ✅ REMOVED: setShowTaskForm(false) - this was hiding the form

          showNotification(`No tasks found for ${weekDate}. Create a new task.`, 'info');
        }
      } catch (error) {
        console.error('❌ Error loading existing tasks:', error);
        showNotification('Failed to load tasks for this week', 'error');
      }
  };

  const handleClearObjective = async () => {
    if (selectedObjective) {
      await deleteDraft(selectedObjective.id, selectedWeek);
      setObjectiveDrafts(prev => ({
        ...prev,
        [selectedObjective.id]: false
      }));
    }

    setSelectedObjective(null);
    setHasSubmittedTaskThisWeek(false);
    setHasUnsavedChanges(false);
    setKpiEnforcementActive(false); // Reset enforcement
    setTasks([{
      id: Date.now().toString(),
      taskName: "",
      taskDescription: "",
      progressPercent: 0,
      status: "",
      achievements: "",
      challenges: "",
      nextSteps: "",
      kpiId: "",
      kpiName: "",
      isNew: true
    }]);
    setSelectedTaskIndex(0);
  };

  useEffect(() => {
    const fetchUser = async () => {
      const userInfo = await getCurrentUserWithRole(engine);
      setCurrentUser(userInfo);
    };

    fetchUser();
  }, [engine]);

  // Load current user position for manager lookup
useEffect(() => {
  const loadPosition = async () => {
    if (!currentUser?.id || !engine) return;
    
    try {
      const response = await engine.query({
        currentUserPosition: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E', // Position TE type
            filter: `V66IFsw5kq6:eq:${currentUser.id}`, // Current Incumbent Staff ID
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            ouMode: 'ACCESSIBLE',
            pageSize: 1
          }
        }
      });
      
      const position = response.currentUserPosition?.trackedEntityInstances?.[0];
      if (position) {
        setCurrentUserPosition(position);
        console.log('✅ [POSITION] Loaded user position:', position.trackedEntityInstance);
      }
    } catch (error) {
      console.error('❌ [POSITION] Failed to load position:', error);
    }
  };
  
  loadPosition();
}, [currentUser?.id, engine]);
  
     
  // Reset challenge tracking when task changes
    useEffect(() => {
      console.log('🔄 [CHALLENGE RESET] Task changed, resetting flags', {
        selectedTaskIndex,
        currentTaskId: currentTask?.id,
        newChallenges: currentTask?.challenges?.substring(0, 30)
      });
      
      previousChallengesRef.current = currentTask?.challenges || '';
      challengeNotificationSentRef.current = false;
      emailInProgressRef.current = false;
    }, [selectedTaskIndex, currentTask?.id]);



  // Fetch objectives
  useEffect(() => {
    const fetchObjectives = async () => {
      if (!currentUser || !fiscalYearLoaded) return;

      try {
        setIsLoading(true);

        const response = await engine.query({
          objectives: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: 'VIBtpvkpHFR',
              fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value],trackedEntityInstance,createdBy,createdByUserInfo,storedBy,lastUpdatedByUserInfo',
              pageSize: 1000,
              order: 'eventDate:desc'
            }
          }
        });

        const processObjectives = async () => {
          const processedObjectives = [];

          console.log('Current user:', currentUser);

          for (const event of response.objectives?.events || []) {
            const dataValues = event.dataValues || [];

            const usernameRaw =
              event.createdByUserInfo?.username ||
              event.storedBy ||
              event.lastUpdatedByUserInfo?.username ||
              null;

            const creatorUsername = usernameRaw ? usernameRaw.toLowerCase().trim() : '';

            console.log('Objective creator username:', creatorUsername, 'Full event:', event);

            const isVisible = shouldShowObjective(
              currentUser.roleId,
              currentUser.username,
              creatorUsername
            );

            console.log('Is visible:', isVisible, 'Current user:', currentUser.username, 'Creator:', creatorUsername);

            if (isVisible) {
              const startDate =
                dataValues.find(dv => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.START_DATE)?.value ||
                event.eventDate;
              const completionDate =
                dataValues.find(dv => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.COMPLETION_DATE)?.value || '';

              processedObjectives.push({
                id: event.event,
                name: dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed Objective',
                description: dataValues.find(dv => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
                orgUnit: event.orgUnit,
                orgUnitName: event.orgUnitName,
                trackedEntityInstance: event.trackedEntityInstance,
                startDate,
                completionDate,
                eventDate: event.eventDate,
                createdBy: creatorUsername
              });
            }
          }

          const fiscalFilteredObjectives = processedObjectives.filter(objective =>
            objectiveBelongsToFiscalYear(objective, fiscalYear)
          );

          console.log('Final processed objectives:', processedObjectives);
          console.log('Fiscal filtered objectives:', fiscalFilteredObjectives);
          return fiscalFilteredObjectives;
        };

        const visibleObjectives = await processObjectives();
        setObjectives(visibleObjectives);

      } catch (error) {
        console.error('Error fetching objectives:', error);
        showNotification('Failed to load objectives', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchObjectives();
  }, [currentUser, engine, fiscalYearLoaded, fiscalYear.start, fiscalYear.end]);

  // Load status options
  useEffect(() => {
    const loadOptions = async () => {
      const options = await fetchStatusOptions();
      const formattedOptions = options.map(opt => ({
        value: opt.code,
        label: opt.name
      }));
      setStatusOptions(formattedOptions);
    };
    loadOptions();
  }, []);

  // Load existing tasks when objective is selected
  useEffect(() => {
    if (!selectedObjective) {
      setExistingTasks([]);
      return;
    }

    const loadExistingTasks = async (objectiveId) => {
      try {
        const response = await engine.query({
          tasks: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: TASK_PROGRAM_STAGE_ID,
              filter: [`plJovUlxfQ0:eq:${objectiveId}`],
              fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
              pageSize: 1000,
              order: `${TASK_DATAELEMENT_MAPPING.selectedWeek}:desc`
            }
          }
        });

        const existingTasksData = response.tasks?.events?.map(event => {
          const dataValues = event.dataValues || [];

          // Parse subtasks
          const subTasksValue = dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.subTasks)?.value;
          let subTasks = [];
          try {
            subTasks = subTasksValue ? JSON.parse(subTasksValue) : [];
          } catch (error) {
            console.warn('Failed to parse subtasks JSON for existing task:', error);
            subTasks = [];
          }

          return {
            id: event.event, // Use database event ID as task ID
            eventId: event.event, // Store event ID
            selectedWeek: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value || 'Unknown date',
            progressPercent: parseInt(dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0,
            status: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || '',
            challenges: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
            nextSteps: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
            achievements: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
            taskName: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || '',
            taskDescription: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
            kpiId: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '',
            subTasks: subTasks,
            isNew: false // Mark as existing database event
          };
        }) || [];

        setExistingTasks(existingTasksData);
        return existingTasksData;

      } catch (error) {
        console.error('Error loading existing tasks:', error);
        return [];
      }
    };

    loadExistingTasks(selectedObjective.id);
  }, [selectedObjective, engine]);

    useEffect(() => {
      const checkObjectivesForTasksAndDrafts = async () => {
        const tasksMap = {};
        const draftsMap = {};

        for (const objective of objectives) {
          try {
            // Check for submitted tasks
            const hasTask = await checkExistingTaskThisWeek(objective.id, selectedWeek);
            tasksMap[objective.id] = hasTask;

            // Check for drafts (only if no submitted task exists)
            if (!hasTask) {
              const hasDraft = await checkObjectiveHasDraft(objective.id, selectedWeek);
              draftsMap[objective.id] = hasDraft;
            } else {
              draftsMap[objective.id] = false;
            }
          } catch (error) {
            console.error(`Error checking task/draft for objective ${objective.id}:`, error);
            tasksMap[objective.id] = false;
            draftsMap[objective.id] = false;
          }
        }

        setObjectiveTasks(tasksMap);
        
        // ONLY update objectiveDrafts for objectives that don't already have a true status
        // This prevents overwriting touched status from localStorage or other weeks
        setObjectiveDrafts(prev => {
          const newDrafts = { ...prev };
          for (const [id, hasDraft] of Object.entries(draftsMap)) {
            // Only set to false if it's currently undefined or false
            // Never overwrite an existing true status
            if (newDrafts[id] !== true) {
              newDrafts[id] = hasDraft;
            }
          }
          return newDrafts;
        });
      };

      if (objectives.length > 0) {
        checkObjectivesForTasksAndDrafts();
      }
    }, [objectives, selectedWeek]);

    // Delay autosave until meaningful content exists (prevents duplicate empty tasks)
      // ============================================================================
    // AUTO-SAVE WITH PAST WEEK SUPPORT - FIXED VERSION
    // ============================================================================
    useEffect(() => {
      // CRITICAL: Check if there's meaningful content AND we have unsaved changes
      const hasMeaningfulContent = tasks.some(task => {
        // Must have valid task name first
        const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task';
        if (!hasValidTaskName) return false;
        
        // CHECK: At least one activity/subtask has meaningful content
        const hasActivityContent = task.subTasks?.some(sub => 
          (sub.description?.trim() && sub.description.trim().length > 5) || // Min 5 chars
          (sub.progressPercent > 0) // Progress updated
        );
        
        // SECONDARY CHECK: Other substantive fields filled (fallback)
        const hasOtherContent = 
          (task.achievements?.trim() && task.achievements.trim().length > 10) ||
          (task.challenges?.trim() && task.challenges.trim().length > 10);
        
        return hasActivityContent || hasOtherContent;
      });

      // FIXED: Check if there are ANY incomplete tasks across all weeks
      const hasAnyIncompleteTask = tasks.some(task => task.progressPercent < 100);
      
      // FIXED: Allow auto-save for ANY incomplete tasks regardless of week
      const shouldAutoSave = 
        hasMeaningfulContent && 
        hasUnsavedChanges && 
        selectedObjective && 
        hasAnyIncompleteTask; // Check ANY incomplete task, not just current task
      
      // Additional check: If we have tasks loaded and they're not all complete, allow auto-save
      const hasExistingTasks = tasks.length > 0;
      const allTasksComplete = tasks.length > 0 && tasks.every(task => task.progressPercent === 100);
      
      if (shouldAutoSave && !allTasksComplete) {
        // Clear any existing timeout to prevent multiple saves
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
        
        // Set new timeout for auto-save
        autoSaveTimeoutRef.current = setTimeout(async () => {
          console.log('✅ AUTOSAVE TRIGGERED:', {
            week: selectedWeek,
            tasksCount: tasks.length,
            incompleteTasks: tasks.filter(t => t.progressPercent < 100).length,
            isPastWeek: isPastWeek(selectedWeek),
            allTasksComplete
          });
          
          try {
            // Save draft first - this will handle both current and past weeks
            await saveDraft(tasks, selectedObjective, selectedWeek);
            
            // Check for challenge notifications for the current task
            const currentChallengesNow = currentTask?.challenges || '';
            const hasContentNow = currentChallengesNow?.trim().length > 0;
            const challengesChangedNow = currentChallengesNow !== previousChallengesRef.current;
            const hasNewChallengesNow = hasContentNow && challengesChangedNow;
            
            if (hasNewChallengesNow && !challengeNotificationSentRef.current && !emailInProgressRef.current) {
              if (currentUserPosition?.trackedEntityInstance) {
                console.log('📧 [CHALLENGE DETECTED] Sending notification');
                emailInProgressRef.current = true;
                
                try {
                  const result = await sendChallengeNotification(
                    currentTask, 
                    selectedObjective, 
                    currentChallengesNow
                  );
                  
                  if (result.success) {
                    challengeNotificationSentRef.current = true;
                    const recipientName = result.recipientName || 'your manager';
                    showNotification(`Challenge notification sent to ${recipientName}`, 'success');
                  } else {
                    showNotification('Failed to send challenge notification', 'warning');
                  }
                } catch (error) {
                  console.error('❌ [EMAIL ERROR]:', error);
                } finally {
                  setTimeout(() => {
                    emailInProgressRef.current = false;
                  }, 2000);
                }
              }
            }
            
            previousChallengesRef.current = currentChallengesNow;
            
          } catch (error) {
            console.error('❌ Auto-save failed:', error);
            showNotification('Failed to auto-save progress', 'warning');
          }
          
        }, AUTO_SAVE_DELAY);
        
        return () => {
          if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
          }
        };
      }
      
      // Log why auto-save was skipped for debugging
      if (hasUnsavedChanges && selectedObjective) {
        console.log('⏸️ AUTOSAVE SKIPPED:', {
          reason: allTasksComplete ? 'All tasks 100% complete' : 'No meaningful content',
          tasksCount: tasks.length,
          allTasksComplete,
          hasMeaningfulContent,
          selectedWeek: selectedWeek,
          isPastWeek: isPastWeek(selectedWeek)
        });
      }
  }, [  tasks, 
        hasUnsavedChanges, 
        selectedObjective, 
        selectedWeek, 
        currentTask?.challenges,
        currentTask?.progressPercent,
        selectedTaskIndex,
        currentTask?.taskName
      ]);


        // Stable subtask callbacks
    const handleUpdateSubTask = useCallback((subTaskIndex, updates) => {
        setTasks(prev => {
          const newTasks = [...prev];
          const currentSubTasks = [...(newTasks[selectedTaskIndex]?.subTasks || [])];
          const updatedSubTask = { ...currentSubTasks[subTaskIndex], ...updates };
          currentSubTasks[subTaskIndex] = updatedSubTask;

          const updatedTask = { ...newTasks[selectedTaskIndex], subTasks: currentSubTasks };
          updatedTask.progressPercent = calculateTaskProgress(currentSubTasks);

          newTasks[selectedTaskIndex] = updatedTask;
          return newTasks;
        });

        // Mark as unsaved but don't trigger immediate re-render of all subtasks
        if (!isFormDisabled() && selectedObjective) {
          setHasUnsavedChanges(true);
        }
      }, [selectedTaskIndex, selectedObjective, isFormDisabled]);

    const handleRemoveSubTask = useCallback((subTaskIndex) => {
        setTasks(prev => {
          const newTasks = [...prev];
          const updatedSubTasks = (newTasks[selectedTaskIndex]?.subTasks || []).filter((_, i) => i !== subTaskIndex);
          const updatedTask = { ...newTasks[selectedTaskIndex], subTasks: updatedSubTasks };
          updatedTask.progressPercent = calculateTaskProgress(updatedSubTasks);
          newTasks[selectedTaskIndex] = updatedTask;
          return newTasks;
        });
        setHasUnsavedChanges(true);
    }, [selectedTaskIndex]);

    
    useEffect(() => {
          if (!selectedObjective) return;
          
          // Comprehensive check for ANY content
          const hasAnyContent = tasks.some(task => {
            const hasValidTaskName = task.taskName?.trim() && 
                                    task.taskName !== 'Unnamed Task' && 
                                    task.taskName !== '';
            const hasTaskDescription = task.taskDescription?.trim() && 
                                      task.taskDescription.trim().length > 3;
            const hasAchievements = task.achievements?.trim() && 
                                    task.achievements.trim().length > 5;
            const hasChallenges = task.challenges?.trim() && 
                                  task.challenges.trim().length > 5;
            const hasActivities = task.subTasks && task.subTasks.length > 0;
            const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;
            
            return hasValidTaskName || hasTaskDescription || hasAchievements || 
                  hasChallenges || hasActivities || hasProgress;
          });
          
          // Only update if value changed
          setObjectiveDrafts(prev => {
            const currentStatus = prev[selectedObjective.id] || false;
            if (currentStatus !== hasAnyContent) {
              console.log(`📝 [TOUCHED] ${selectedObjective.name}: ${hasAnyContent ? 'TOUCHED ✅' : 'NOT TOUCHED ⚠️'}`);
              return { ...prev, [selectedObjective.id]: hasAnyContent };
            }
            return prev;
          });
        }, [tasks, selectedObjective]);
        

        // 4. Also check existingTasks for touched status (for objectives not currently selected)
        useEffect(() => {
          if (existingTasks.length === 0) return;
          
          // Group existing tasks by objective ID
          const tasksByObjective = {};
          existingTasks.forEach(task => {
            if (task.objectiveId) {
              if (!tasksByObjective[task.objectiveId]) {
                tasksByObjective[task.objectiveId] = [];
              }
              tasksByObjective[task.objectiveId].push(task);
            }
          });
          
          // Update status for objectives that have content
          Object.entries(tasksByObjective).forEach(([objectiveId, objectiveTasks]) => {
            const hasContent = objectiveTasks.some(task => 
              task.taskName?.trim() && task.taskName !== 'Unnamed Task' ||
              task.subTasks?.length > 0 ||
              task.progressPercent > 0 ||
              (task.achievements?.trim()?.length > 0) ||
              (task.challenges?.trim()?.length > 0)
            );
            
            if (hasContent) {
              setObjectiveDrafts(prev => {
                if (!prev[objectiveId]) {
                  console.log(`📝 [TOUCHED from existing tasks] Objective ${objectiveId}: TOUCHED ✅`);
                  return { ...prev, [objectiveId]: true };
                }
                return prev;
              });
            }
          });
     }, [existingTasks]);

    useEffect(() => {
      if (!selectedObjective) return;
      if (tasks.length === 0) return;
      
      // Force check for content right after tasks load
      const hasAnyContent = tasks.some(task => {
        const hasValidTaskName = task.taskName?.trim() && 
                                task.taskName !== 'Unnamed Task' && 
                                task.taskName !== '';
        const hasTaskDescription = task.taskDescription?.trim() && task.taskDescription.trim().length > 3;
        const hasAchievements = task.achievements?.trim() && task.achievements.trim().length > 5;
        const hasChallenges = task.challenges?.trim() && task.challenges.trim().length > 5;
        const hasActivities = task.subTasks && task.subTasks.length > 0;
        const hasProgress = task.progressPercent > 0 && task.progressPercent < 100;
        
        return hasValidTaskName || hasTaskDescription || hasAchievements || 
              hasChallenges || hasActivities || hasProgress;
      });
      
      console.log(`🔍 [INITIAL LOAD] ${selectedObjective.name}: hasContent = ${hasAnyContent}`);
      
      setObjectiveDrafts(prev => {
        const currentStatus = prev[selectedObjective.id] || false;
        if (currentStatus !== hasAnyContent) {
          return {
            ...prev,
            [selectedObjective.id]: hasAnyContent
          };
        }
        return prev;
      });
    }, [selectedObjective, tasks]);

    useEffect(() => {
        if (hasUnsavedChanges && selectedObjective) {
          // Clear any existing timeout
          if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
          }
          
          // Set new timeout for auto-save
          autoSaveTimeoutRef.current = setTimeout(async () => {
            console.log('🔄 Auto-saving due to field changes...');
            try {
              await saveDraft(tasks, selectedObjective, selectedWeek);
            } catch (error) {
              console.error('Auto-save failed:', error);
            }
          }, AUTO_SAVE_DELAY);
        }
        
        return () => {
          if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
          }
        };
   }, [hasUnsavedChanges, tasks, selectedObjective, selectedWeek]);


  // Also stabilize task-level update if used elsewhere
    const updateCurrentTask = useCallback((updates) => {
      setTasks(prev => {
        const newTasks = [...prev];
        const updatedTask = { ...newTasks[selectedTaskIndex], ...updates };
        if (updates.subTasks !== undefined) {
          updatedTask.progressPercent = calculateTaskProgress(updatedTask.subTasks);
        }
        newTasks[selectedTaskIndex] = updatedTask;
        return newTasks;
      });

      // CRITICAL: Always mark as unsaved when user makes changes (unless form is disabled)
      if (!isFormDisabled()) {
        setHasUnsavedChanges(true);
      }
    }, [selectedTaskIndex, isFormDisabled]);

  // Add KPI progress calculation
  const calculateKPIProgress = () => {
    const kpiProgress = {};

   
    const allTasks = [
      ...tasks.filter(task => task.kpiId && task.progressPercent > 0),
      ...existingTasks.filter(task => task.kpiId && task.progressPercent > 0)
    ];

    allTasks.forEach(task => {
      if (task.kpiId && task.progressPercent > 0) {
        if (!kpiProgress[task.kpiId]) {
          kpiProgress[task.kpiId] = {
            kpiName: task.kpiName || 'Unknown KPI',
            totalProgress: 0,
            taskCount: 0,
            tasks: []
          };
        }

        kpiProgress[task.kpiId].totalProgress += task.progressPercent;
        kpiProgress[task.kpiId].taskCount += 1;
        kpiProgress[task.kpiId].tasks.push({
          taskName: task.taskName,
          progress: task.progressPercent,
          isSubmitted: !task.isNew
        });
      }
    });

    // Calculate average progress for each KPI
    Object.keys(kpiProgress).forEach(kpiId => {
      kpiProgress[kpiId].averageProgress =
        kpiProgress[kpiId].totalProgress / kpiProgress[kpiId].taskCount;
    });

    return kpiProgress;
  };

  // KPI Progress Display Component
  const KPIProgressDisplay = () => {
    const kpiProgress = calculateKPIProgress();

    if (Object.keys(kpiProgress).length === 0) {
      return null;
    }

    return (
      <div style={{
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>
          KPI Progress Summary
        </h4>
        <div style={{ display: 'grid', gap: '12px' }}>
          {Object.entries(kpiProgress).map(([kpiId, data]) => (
            <div key={kpiId} style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong>{data.kpiName}</strong>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: '#2980b9',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.8rem'
                }}>
                  Average: {data.averageProgress.toFixed(1)}%
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {data.taskCount} task(s) contributing to this KPI
              </div>
              <div style={{ marginTop: '8px' }}>
                {data.tasks.map((task, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.7rem',
                    padding: '2px 0'
                  }}>
                    <span>{task.taskName}</span>
                    <span>{task.progress}%</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
  };


  const disabledStyle = {
    backgroundColor: '#f8f9fa',
    cursor: 'not-allowed',
    opacity: 0.7
  };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontWeight: '400', marginBottom: '2px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );


  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <CircularLoader small />
        <span style={{ marginLeft: '12px' }}>Loading objectives...</span>
      </div>
    );
  }

  return (
      <DndProvider backend={HTML5Backend}>
        <div
          className="weekly-tracker-container"
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '24px',
            backgroundColor: '#ffffffff',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            minHeight: '100vh',
          }}
        >
          <h4
            className="section-title"
            style={{
              textAlign: 'center',
              color: '#297934ff',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '2px solid #dfe3f7ff',
              fontSize: window.innerWidth < 768 ? '1rem' : '1.1rem',
            }}
          >
            Weekly Tasks Capture
          </h4>

          {notification && (
            <div
              className="notification-toast"
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                left: 'auto',
                zIndex: 10000,
                width: 'auto',
                maxWidth: window.innerWidth < 768 ? 'calc(100vw - 40px)' : '400px',
                minWidth: window.innerWidth < 480 ? '250px' : '300px',
              }}
            >
              <AlertBar
                {...(notification.type === 'success'
                  ? { success: true }
                  : { warning: true })}
                onHidden={() => setNotification(null)}
                style={{ marginBottom: '16px' }}
              >
                {notification.message}
              </AlertBar>
            </div>
          )}

          {/* Two-column layout - responsive */}
          <div 
            className="two-column-layout"
            style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr', 
              gap: '24px' 
            }}
          >
         {/* Update the week selector to show edit status clearly */}
         {selectedObjective && (
          <WeekChipSelector
            weeks={allWeeks}
            selectedWeek={selectedWeek}
            onWeekSelect={(week) => {
              setWeekEnding(week);
              setHasSubmittedTaskThisWeek(false); // Reset when changing weeks
              setShowTaskForm(true); // Reset form visibility
              loadExistingTasksForWeek(selectedObjective.id, week);
            }}
            existingTasks={existingTasks}
          />
        )}

            {/* Step 1: Select Objective */}
            {currentStep === 'SELECT_OBJECTIVE' && (
              <div>
                <h4 style={{ 
                  marginBottom: '16px', 
                  color: '#2c3e50',
                  fontSize: window.innerWidth < 768 ? '0.9rem' : '1.0rem'
                }}>
                  Your Objectives
                </h4>
        
                {/* Objective Selection Zone */}
                <ObjectiveSelectionDisplay
                  selectedObjective={selectedObjective}
                  onClear={handleClearObjective}
                  hasTaskThisWeek={hasSubmittedTaskThisWeek}
                />

                {/* Search Box */}
                {renderFormField(
                  'Search Objective',
                  <div style={{ 
                    position: 'relative', 
                    flex: 1,
                    width: '100%' 
                  }}>
                    <IconSearch24 style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#666',
                      pointerEvents: 'none',
                      zIndex: 10,
                      width: '20px',
                      height: '20px'
                    }} />
                    <Input
                      type="text"
                      placeholder="Search objectives..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{ 
                        paddingLeft: '3.5rem', 
                        paddingRight: '1rem',
                        fontSize: window.innerWidth < 768 ? '14px' : '16px',
                      }}
                    />
                  </div>,
                  false
                )}
                
                {/* Objectives List - Mobile optimized */}
                <div
                  style={{
                    maxHeight: window.innerWidth < 768 ? '400px' : '500px',
                    overflowY: 'auto',
                    padding: window.innerWidth < 768 ? '8px' : '12px',
                    border: '1px solid #aeb8f1ff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    backgroundColor: '#e9f7efff',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {filteredObjectives.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#e79506ff',
                        fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
                      }}
                    >
                      {objectives.length === 0
                        ? 'No objectives found. Create an objective first.'
                        : 'No objectives match your search'}
                    </div>
                  ) : (
                    filteredObjectives.map((objective) => (
                      <DraggableObjectiveCard
                          key={objective.id}
                          objective={objective}
                          isSelected={selectedObjective?.id === objective.id}
                          onSelect={(obj) => {
                            handleObjectiveSelect(obj);
                            setCurrentStep('SELECT_KPI');
                          }}
                          hasSubmittedTaskThisWeek={objectiveTasks[objective.id] || false}
                          hasDraftThisWeek={objectiveDrafts[objective.id] || false}
                          isWeekLocked={isSpecificWeekLocked(selectedWeek)}
                          currentTasks={selectedObjective?.id === objective.id ? tasks : []} // Only pass tasks for selected objective
                          selectedWeek={selectedWeek}
                        />
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Select KPI - Mobile optimized with animated finger pointer */}
            {currentStep === 'SELECT_KPI' && selectedObjective && (
              <div>
                <div
                  style={{
                    marginBottom: '24px',
                    color: '#0e6c05ff',
                    padding: window.innerWidth < 768 ? '12px' : '16px',
                    backgroundColor: '#caf5bfff',
                    borderRadius: '8px',
                    fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
                  }}
                >
                  <strong>Selected Objective:</strong> {selectedObjective.name}
                </div>

                <div style={{ position: 'relative', zIndex: 1001 }}>
                  <button
                    onClick={handleBackToObjective} 
                    style={{
                      marginBottom: '16px',
                      backgroundColor: '#053e70ff',
                      color: '#f6f7f9ff',
                      borderRadius: '8px',
                      padding: window.innerWidth < 768 ? '10px 16px' : '8px 16px',
                      border: 'none',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      width: window.innerWidth < 768 ? '100%' : 'auto',
                      fontSize: window.innerWidth < 768 ? '14px' : '16px',
                    }}
                  >
                    ← Back to Tasks Main Page
                  </button>
                </div>

                {/* Animated Instruction Section */}
                <div style={{
                  backgroundColor: '#fff8e1',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  border: '2px solid #ff9800',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Animated finger pointing down/right */}
                  <div style={{ 
                    fontSize: '1.2rem', 
                    fontWeight: 'bold', 
                    color: '#e65100',
                    marginTop: '8px'
                  }}>
                    Tap to link KPI to this task
                  </div>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#bf360c',
                    marginTop: '8px'
                  }}>
                    Select a KPI from below to associate with your task
                  </div>
                                  
                  <div className="finger-pointer" style={{ fontSize: '4rem' }}>
                    <TbHandFingerDown size={30}/>
                  </div>
                </div>

                {kpis.length > 0 ? (
                  <div
                    className="kpi-grid"
                    style={{
                      maxHeight: window.innerWidth < 768 ? '400px' : '300px',
                      overflowY: 'auto',
                      backgroundColor: 'rgb(229, 225, 223)',
                      borderRadius: '8px',
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: window.innerWidth < 768 ? '12px' : '18px',
                      padding: window.innerWidth < 768 ? '12px' : '16px',
                      WebkitOverflowScrolling: 'touch',
                    }}
                  >
                    {kpis.map((kpi, index) => (
                      <div
                        key={kpi.id}
                        className="kpi-card-highlight"
                        style={{
                          animation: index === 0 ? 'pulse-ring 2s ease-in-out infinite' : 'none',
                          borderRadius: '14px'
                        }}
                      >
                        <DraggableKPICard
                          kpi={kpi}
                          isSelected={selectedKPI?.eventId === kpi.eventId}
                          onSelect={(kpi) => {
                            setSelectedKPI(kpi);
                            updateCurrentTask({
                              kpiId: kpi.eventId,
                              kpiName: kpi.name,
                            });
                            setCurrentStep('FILL_TASK');
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                  }}>
                    <p>No KPIs found for this objective.</p>
                    <p style={{ fontSize: '0.85rem', color: '#666' }}>
                      Please create KPIs first in the KPI Management section.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Fill Task Form - Mobile optimized */}
            {currentStep === 'FILL_TASK' && selectedObjective && selectedKPI && (
              <>
                {isPastWeek(selectedWeek) && (
                  <AlertBar
                    warning
                    style={{
                      marginBottom: '16px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      fontSize: window.innerWidth < 768 ? '0.85rem' : '1rem',
                    }}
                  >
                    <strong> Past Week Editing:</strong> You can only edit existing tasks from this week.
                  </AlertBar>
                )}

                <div
                  style={{
                    marginBottom: '24px',
                    color: '#0e6c05ff',
                    padding: window.innerWidth < 768 ? '12px' : '16px',
                    backgroundColor: '#caf5bfff',
                    borderRadius: '8px',
                    fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Objective:</strong> {selectedObjective.name}
                  </div>
                  <div>
                    <strong>KPI:</strong> {selectedKPI.name}
                  </div>
                </div>

                <AutoSaveIndicator
                  isAutoSaving={isAutoSaving}
                  hasUnsavedChanges={hasUnsavedChanges}
                  lastAutoSaved={lastAutoSaved}
                  isFormDisabled={isFormDisabled}
                  customMessage={isViewOnlyWeek(selectedWeek)
                    ? "Editing existing task from past week"
                    : undefined}
                />

                {/* Floating Progress Bar - Sticky at top */}
                  <div style={{
                    position: 'sticky',
                    top: '0',
                    zIndex: 99,
                    border: '2px',
                    marginBottom: '16px',
                    backgroundColor: '#031840',
                    padding: '8px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgb(54, 40, 131)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#fff' }}>
                        Overal Progress:
                      </span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 12px',
                        backgroundColor: getProgressColor(currentTask.progressPercent),
                        borderRadius: '24px',
                        color: 'white'
                      }}>
                        <span style={{ fontWeight: 'bold' }}>{currentTask.progressPercent}%</span>
                      </div>
                    </div>
                    {currentTask.subTasks?.length > 0 && (
                      <div style={{ fontSize: '14px', color: '#5eed80', display: 'flex', gap: 4, lineHeight: 1 }}>
                        <CheckCircleIcon size={16} /> Auto-calculated
                      </div>
                    )}
                  </div>

                {/* Task Navigator for Multiple Tasks - Mobile optimized with visual indicators */}
                {hasSubmittedTaskThisWeek && !showTaskForm && (
                  <div className="task-navigator-card" style={{
                    marginBottom: '24px',
                    padding: window.innerWidth < 768 ? '12px' : '16px',
                    border: '2px solid #2196f3',
                    borderRadius: '12px',
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                      justifyContent: 'space-between', 
                      alignItems: window.innerWidth < 768 ? 'flex-start' : 'center',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #bbdefb',
                      gap: window.innerWidth < 768 ? '8px' : '0'
                    }}>
                      <h4 style={{ 
                        margin: 0, 
                        color: '#2e5999',
                        fontSize: window.innerWidth < 768 ? '0.8rem' : '1.0rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        <span style={{ fontSize: '1.5rem' }}><GiNotebook size={24} /></span>
                        Tasks ({tasks.length})
                      </h4>
                      <span style={{
                        backgroundColor: '#6493c9',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold'
                      }}>
                        {tasks.filter(t => t.subTasks?.length > 0).length} with activities
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {tasks.map((task, index) => {
                        const hasActivities = task.subTasks?.length > 0;
                        const isActive = selectedTaskIndex === index;
                        
                        // Check if this task is from a past week and incomplete
                        const isTaskWeek = task.selectedWeek || selectedWeek;
                        const isPastWeekTask = isPastWeek(isTaskWeek);
                        const isIncomplete = task.progressPercent < 100;
                        const needsAttention = isPastWeekTask && isIncomplete;
                        
                        return (
                          <div
                            key={task.id || index}
                            onClick={() => {
                              setSelectedTaskIndex(index);
                              setShowTaskForm(true); 
                            }}
                            className="task-card-content"
                            style={{
                              position: 'relative', // Important for absolute positioning of badge
                              padding: window.innerWidth < 768 ? '12px' : '16px',
                              border: isActive 
                                ? '2px solid #0d47a1' 
                                : needsAttention
                                  ? '2px solid #ff9800'  // Orange border for tasks needing attention
                                  : hasActivities 
                                    ? '1px solid #90caf9' 
                                    : '1px dashed #bbdefb',
                              borderRadius: '10px',
                              backgroundColor: isActive 
                                ? '#bbdefb' 
                                : needsAttention
                                  ? '#fff3e0'  // Light orange background for past week incomplete tasks
                                  : 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: window.innerWidth < 768 ? 'stretch' : 'center',
                              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                              gap: window.innerWidth < 768 ? '12px' : '0',
                              overflow: 'hidden'
                            }}
                          >
                            {/* Visual indicator bar on the left */}
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              backgroundColor: needsAttention 
                                ? '#ff9800'  // Orange for past week incomplete
                                : hasActivities 
                                  ? (isActive ? '#0d47a1' : '#2196f3') 
                                  : '#bdbdbd'
                            }} />
                            
                            {/* IN PROGRESS Badge for past week incomplete tasks */}
                            {needsAttention && (
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                backgroundColor: '#ff9800',
                                color: 'white',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                zIndex: 1,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span>IN PROGRESS</span>
                              </div>
                            )}
                            
                            {/* Task completion badge for 100% tasks */}
                            {task.progressPercent === 100 && (
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                backgroundColor: '#4caf50',
                                color: 'white',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                zIndex: 1,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span>✓</span>
                                <span>COMPLETE</span>
                              </div>
                            )}
                            
                            <div style={{ flex: 1, paddingLeft: window.innerWidth < 768 ? '8px' : '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                <div style={{
                                  width: window.innerWidth < 768 ? '28px' : '32px',
                                  height: window.innerWidth < 768 ? '28px' : '32px',
                                  borderRadius: '50%',
                                  backgroundColor: needsAttention 
                                    ? '#ff9800' 
                                    : isActive 
                                      ? '#0d47a1' 
                                      : '#e3f2fd',
                                  color: needsAttention || isActive ? 'white' : '#0d47a1',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  flexShrink: 0,
                                  border: needsAttention || isActive ? '2px solid white' : '2px solid #0d47a1',
                                  fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem'
                                }}>
                                  {index + 1}
                                </div>
                                <strong style={{ 
                                  color: needsAttention ? '#e65100' : (isActive ? '#0a2e5e' : '#2c3e50'),
                                  fontSize: window.innerWidth < 768 ? '0.95rem' : '1.1rem',
                                  flex: 1,
                                  wordBreak: 'break-word'
                                }}>
                                  {task.taskName || `Task ${index + 1}`}
                                </strong>
                              </div>
                              
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap',
                                gap: window.innerWidth < 768 ? '8px' : '20px', 
                                fontSize: window.innerWidth < 768 ? '0.85rem' : '0.95rem', 
                                color: '#424242',
                                marginTop: '8px'
                              }}>
                                <span> <ActivityIcon size={16} /> Activities: <strong>{task.subTasks?.length || 0}</strong></span>
                                <span> <GiProgression size={16} /> Progress: <strong>{task.progressPercent || 0}%</strong></span>
                                {task.kpiName && window.innerWidth > 480 && (
                                  <span style={{ maxWidth: '40%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    KPI: <em>{task.kpiName}</em>
                                  </span>
                                )}
                              </div>
                              
                              {/* Warning message for past week incomplete tasks */}
                              {needsAttention && (
                                <div style={{ 
                                  marginTop: '8px', 
                                  padding: '6px 12px', 
                                  backgroundColor: '#fff3e0', 
                                  borderRadius: '6px', 
                                  fontSize: '0.75rem',
                                  color: '#e65100',
                                  borderLeft: '3px solid #ff9800'
                                }}>
                                  ⚠️ This task is from a past week and still in progress. Please complete it.
                                </div>
                              )}
                              
                              {/* No activities warning */}
                              {!hasActivities && !needsAttention && (
                                <div style={{ 
                                  marginTop: '8px', 
                                  padding: '6px 12px', 
                                  backgroundColor: '#fff8e1', 
                                  borderRadius: '6px', 
                                  fontSize: '0.8rem',
                                  color: '#5d4037',
                                  borderLeft: '3px solid #ffc107'
                                }}>
                                  ⚠️ No activities added yet
                                </div>
                              )}
                            </div>
                            
                            <Button
                              small
                              primary
                              className="task-badge"
                              style={{ 
                                minWidth: window.innerWidth < 768 ? '100%' : '90px',
                                backgroundColor: needsAttention 
                                  ? '#ff9800' 
                                  : isActive 
                                    ? '#0d47a1' 
                                    : 'white',
                                color: needsAttention || isActive ? 'white' : '#0d47a1',
                                border: `1px solid ${needsAttention ? '#ff9800' : (isActive ? '#0a2e5e' : '#2196f3')}`,
                                fontWeight: 'bold',
                                boxShadow: needsAttention || isActive ? '0 2px 6px rgba(0,0,0,0.3)' : 'none'
                              }}
                            >
                              {needsAttention ? 'COMPLETE NOW' : (isActive ? 'ACTIVE' : 'VIEW')}
                            </Button>
                          </div>
                        );
                      })}
                    </div>       
                    
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '12px', 
                      backgroundColor: '#bbdefb', 
                      borderRadius: '8px',
                      fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem',
                      color: '#0a2e5e',
                      borderLeft: '3px solid #0d47a1'
                    }}>
                      💡 <strong>Tip:</strong> Tap any task above to view/edit its activities. 
                      {tasks.some(t => isPastWeek(t.selectedWeek || selectedWeek) && t.progressPercent < 100) && (
                        <span style={{ display: 'block', marginTop: '4px', color: '#ff9800' }}>
                          ⚠️ Tasks with orange borders are from past weeks and need to be completed.
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation Buttons for Task List View - Always visible when viewing tasks */}
                {hasSubmittedTaskThisWeek && !showTaskForm && (
                  <div className="form-actions" style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    marginTop: '24px',
                    marginBottom: '24px',
                    flexDirection: window.innerWidth < 768 ? 'column' : 'row'
                  }}>
                    <button
                      onClick={() => setCurrentStep('SELECT_KPI')}
                      style={{
                        backgroundColor: '#053e70ff',
                        color: '#f6f7f9ff',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        border: 'none',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        cursor: 'pointer',
                        fontSize: window.innerWidth < 768 ? '14px' : '16px',
                        width: window.innerWidth < 768 ? '100%' : 'auto',
                      }}
                    >
                      ← Back to KPIs
                    </button>
                    <button
                      onClick={handleBackToObjective}
                      style={{
                        backgroundColor: '#6c757d',
                        color: '#f6f7f9ff',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        border: 'none',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        cursor: 'pointer',
                        fontSize: window.innerWidth < 768 ? '14px' : '16px',
                        width: window.innerWidth < 768 ? '100%' : 'auto',
                      }}
                    >
                      ← Back to Objectives
                    </button>
                    {onCancel && (
                      <Button 
                        warning 
                        onClick={onCancel}
                        style={{ width: window.innerWidth < 768 ? '100%' : 'auto' }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                )}

                {showTaskForm && (
                  <>
                    {/* Task Name */}
                    {renderFormField(
                      'Task Name',
                      <Input
                        className="task-name-input"
                        value={currentTask.taskName}
                        onChange={({ value }) => {
                          if (!isPastWeek(selectedWeek) || currentTask.taskName) {
                            updateCurrentTask({ taskName: value })
                          }
                        }}
                        placeholder={isPastWeek(selectedWeek) && !currentTask.taskName ? "Cannot add new tasks in past weeks" : "Task name"}
                        disabled={isFormDisabled() || (isPastWeek(selectedWeek) && !currentTask.taskName)}
                        required
                        inputStyle={{
                          fontSize: window.innerWidth < 768 ? '14px' : '16px',
                        }}
                      />
                    )}

                    {/* Task Description */}
                    {renderFormField(
                      'Task Description',
                      <TextArea
                        value={currentTask.taskDescription}
                        onChange={({ value }) =>
                          updateCurrentTask({ taskDescription: value })
                        }
                        placeholder="Task description"
                        rows={window.innerWidth < 768 ? 2 : 3}
                        disabled={isFormDisabled()}
                        required
                      />
                    )}

                    {/* Activities Section - Mobile optimized */}
                    <div style={{ 
                      marginBottom: '20px', 
                      border: '1px solid #ddd', 
                      borderRadius: '8px', 
                      padding: window.innerWidth < 768 ? '12px' : '16px' 
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                        justifyContent: 'space-between', 
                        alignItems: window.innerWidth < 768 ? 'stretch' : 'center', 
                        marginBottom: '12px',
                        gap: window.innerWidth < 768 ? '8px' : '0'
                      }}>
                        <h4 style={{ margin: 0, color: '#2c3e50', fontSize: window.innerWidth < 768 ? '1rem' : '1.1rem' }}>
                          Activities ({currentTask.subTasks?.length || 0})
                        </h4>

                        {/* FIXED: Show Add Activity button for current weeks OR if no task exists yet */}
                        {(!isPastWeek(selectedWeek) && !isFormDisabled()) && (
                          <Button
                            small
                            primary
                            onClick={() => {
                              updateCurrentTask({
                                subTasks: [...(currentTask.subTasks || []), {
                                  id: Date.now().toString(),
                                  name: '',
                                  description: '',
                                  progressPercent: 0,
                                  completed: false
                                }]
                              });
                            }}
                            style={{ width: window.innerWidth < 768 ? '100%' : 'auto' }}
                          >
                            + Add Activity
                          </Button>
                        )}

                        {/* Show message for past weeks */}
                        {isPastWeek(selectedWeek) && (
                          <span style={{ color: '#666', fontSize: '0.8rem', fontStyle: 'italic' }}>
                            (No new activities can be added to past weeks)
                          </span>
                        )}
                      </div>

                      {/* SubTasks */}
                      <div style={{ maxHeight: window.innerWidth < 768 ? '400px' : 'none', overflowY: 'auto' }}>
                        {currentTask.subTasks?.length > 0 ? (
                          currentTask.subTasks.map((activity, index) => (
                            <SubTask
                              key={activity.id}
                              id={activity.id}
                              name=""
                              description={activity.description}
                              progressPercent={activity.progressPercent}
                              completed={activity.completed}
                              index={index}
                              onUpdate={handleUpdateSubTask}
                              onRemove={handleRemoveSubTask}
                              isFormDisabled={isFormDisabled()}
                              selectedWeek={selectedWeek}
                              showNotification={showNotification}
                            />
                          ))
                        ) : (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px 20px',
                            color: '#999',
                            fontStyle: 'italic'
                          }}>
                            {isPastWeek(selectedWeek) 
                              ? 'No activities exist for this past week'
                              : 'Click "+ Add Activity" to start adding activities for this task'
                            }
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Achievements */}
                    {renderFormField(
                      'Task Achievements / Milestone',
                      <TextArea
                        value={currentTask.achievements}
                        onChange={({ value }) =>
                          updateCurrentTask({ achievements: value })
                        }
                        placeholder="What was accomplished?"
                        rows={window.innerWidth < 768 ? 2 : 3}
                        disabled={isFormDisabled()}
                      />
                    )}

                    {/* Challenges */}
                    {renderFormField(
                      'Task Challenges / Risks',
                      <TextArea
                        value={currentTask.challenges}
                        onChange={({ value }) =>
                          updateCurrentTask({ challenges: value })
                        }
                        placeholder="What obstacles were faced?"
                        rows={window.innerWidth < 768 ? 2 : 3}
                        disabled={isFormDisabled()}
                      />
                    )}

                    {/* Back button when viewing existing tasks */}
                    {hasSubmittedTaskThisWeek && (
                      <button 
                        onClick={() => setShowTaskForm(false)}
                        style={{
                          marginBottom: '16px',
                          backgroundColor: '#053e70ff',
                          color: '#f6f7f9ff',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          border: 'none',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          width: window.innerWidth < 768 ? '100%' : 'auto',
                          fontSize: window.innerWidth < 768 ? '14px' : '16px',
                        }}
                      >
                        ← Back to Tasks List
                      </button>
                    )}

                    {/* Navigation Buttons - Mobile optimized */}
                    <div className="form-actions" style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      marginTop: '24px',
                      flexDirection: window.innerWidth < 768 ? 'column' : 'row'
                    }}>
                      <button
                        onClick={() => setCurrentStep('SELECT_KPI')}
                        style={{
                          backgroundColor: '#053e70ff',
                          color: '#f6f7f9ff',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          border: 'none',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          fontSize: window.innerWidth < 768 ? '14px' : '16px',
                          width: window.innerWidth < 768 ? '100%' : 'auto',
                        }}
                      >
                        ← Back to KPIs
                      </button>
                      {onCancel && (
                        <Button 
                          warning 
                          onClick={onCancel}
                          style={{ width: window.innerWidth < 768 ? '100%' : 'auto' }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Empty State - Mobile optimized */}
            {currentStep === 'SELECT_OBJECTIVE' &&
              !selectedObjective &&
              objectives.length > 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: window.innerWidth < 768 ? '40px 20px' : '60px 20px',
                    color: '#bdc3c7',
                    backgroundColor: '#edf1e3ff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    border: '1px solid #ddd',
                  }}
                >
                  <h3 style={{ 
                    color: '#0b5109ff',
                    fontSize: window.innerWidth < 768 ? '0.9rem' : '1.0rem'
                  }}>
                    Select an Objective
                  </h3>
                  <p style={{ 
                    fontSize: window.innerWidth < 768 ? '0.7rem' : '1.0rem', 
                    marginTop: '8px', 
                    color: '#053006ff' 
                  }}>
                    Your progress will be automatically saved as you type
                  </p>
                </div>
              )}
          </div>
          
          {/* Task Detail Modal */}
          {isTaskModalOpen && (
            <TaskDetailModal
              task={selectedTask}
              statusOptions={statusOptions}
              onClose={handleCloseTaskModal}
            />
          )}
        </div>
      </DndProvider>
  );
}

export default WeeklyProgressTracker;
